import axios, { type AxiosInstance } from 'axios'
import { login } from './auth'

const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  timeout: 600000, // 10 minutes for slow AI operations
  headers: {
    'Content-Type': 'application/json',
    // Версия протокола совместного редактирования: сервер отклоняет запись
    // совместных полей из старых версий страницы (их снимки сбрасывали бы
    // документ у всех участников)
    'X-Collab-Protocol': '2',
  },
  withCredentials: true, // Send session cookie with all requests
  paramsSerializer: {
    indexes: null, // Serialize arrays as repeat keys: status=a&status=b (not status[]=a)
  },
})

// Flag to prevent multiple login redirects
let isRedirecting = false

// Department ID getter (injected by DepartmentStore after init)
let getDepartmentId: (() => string | undefined) | null = null

/**
 * Set the department ID getter function.
 * Called from DepartmentStore after initialization.
 */
export function setDepartmentIdGetter(getter: () => string | undefined) {
  getDepartmentId = getter
}

// Add X-Department-Id header to all requests
// Use { params: { _allDepartments: true } } to skip department filter for cross-department searches
api.interceptors.request.use((config) => {
  // Allow skipping department filter for cross-department searches (e.g., task picker in ideas)
  if (config.params?._allDepartments) {
    delete config.params._allDepartments
    return config
  }
  if (getDepartmentId) {
    const departmentId = getDepartmentId()
    console.log('[API Interceptor] getDepartmentId:', departmentId, 'for', config.url)
    if (departmentId) {
      config.headers['X-Department-Id'] = departmentId
    }
  } else {
    console.log('[API Interceptor] getDepartmentId NOT SET for', config.url)
  }
  return config
})

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !isRedirecting) {
      // Don't redirect on public routes — they work without auth
      if (window.location.pathname.startsWith('/public/')) {
        return Promise.reject(error)
      }
      // Session expired - redirect to login (only once)
      isRedirecting = true
      console.warn('Unauthorized request - redirecting to login')
      // Small delay to allow pending requests to fail gracefully
      setTimeout(() => login(), 100)
    }
    return Promise.reject(error)
  }
)

export { api }

// Auth API (impersonation, etc.)
export const authApi = {
  impersonate: (targetUserId: string) =>
    api.post<{ impersonationToken: string; targetUser: { id: string; name: string; email: string } }>('/auth/impersonate', { targetUserId }),
}

// API endpoints
export interface MergeResult {
  target: Record<string, unknown>
  mergedCounts: {
    comments: number
    attachments: number
    links: number
    owners: number
    systems: number
    contractors: number
    projects: number
  }
}

export interface TaskWatcher {
  personId: string
  name: string
  avatarUrl: string | null
  addedById: string | null
  createdAt: string
}

export const tasksApi = {
  getAll: (params?: Record<string, unknown>) => api.get('/tasks', { params }),
  getTree: (params?: Record<string, unknown>) => api.get('/tasks/tree', { params }),
  getFilteredGraph: (params?: Record<string, unknown>) => api.get('/tasks/graph', { params }),
  getOne: (id: string) => api.get(`/tasks/${id}`),
  getByHumanId: (humanId: string) => api.get(`/tasks/by-human-id/${humanId}`),
  foreignSources: (taskIds: string[]) =>
    api.post<Record<string, { departmentId: string; departmentName: string }>>('/tasks/foreign-sources', { taskIds }),
  create: (data: Record<string, unknown>) => api.post('/tasks', data),
  createDraft: () => api.post('/tasks/draft'),
  publishDraft: (id: string) => api.post(`/tasks/${id}/publish`),
  update: (id: string, data: Record<string, unknown>) => api.patch(`/tasks/${id}`, data),
  delete: (id: string) => api.delete(`/tasks/${id}`),
  getGraph: (id: string) => api.get(`/tasks/${id}/graph`),
  getFullGraph: (id: string, maxDepth?: number) => api.get(`/tasks/${id}/full-graph`, { params: maxDepth ? { maxDepth } : {} }),
  getTags: () => api.get('/tasks/tags'),
  getProjects: (id: string) => api.get(`/tasks/${id}/projects`),
  // Task Owners/Assignees
  getOwners: (id: string) => api.get(`/tasks/${id}/owners`),
  addOwner: (id: string, personId: string, role?: 'owner' | 'contributor' | 'tester' | 'reviewer') =>
    api.post(`/tasks/${id}/owners`, { personId, role: role || 'contributor' }),
  removeOwner: (id: string, personId: string) => api.delete(`/tasks/${id}/owners/${personId}`),
  updateOwnerRole: (id: string, personId: string, role: 'owner' | 'contributor' | 'tester' | 'reviewer') =>
    api.patch(`/tasks/${id}/owners/${personId}`, { role }),
  // Наблюдатели: без personId — подписать себя
  getWatchers: (id: string) => api.get<TaskWatcher[]>(`/tasks/${id}/watchers`),
  addWatcher: (id: string, personId?: string) =>
    api.post<TaskWatcher[]>(`/tasks/${id}/watchers`, personId ? { personId } : {}),
  removeWatcher: (id: string, personId: string) =>
    api.delete<TaskWatcher[]>(`/tasks/${id}/watchers/${personId}`),
  // GitLab Commits
  getCommits: (id: string) => api.get<GitLabCommit[]>(`/tasks/${id}/commits`),
  unlinkCommit: (taskId: string, commitId: string) => api.delete(`/tasks/${taskId}/commits/${commitId}`),
  // Merge
  merge: (targetId: string, sourceId: string) => api.post<MergeResult>(`/tasks/${targetId}/merge/${sourceId}`),
  // Delta sync
  getDeletedSince: (since: string) => api.get<{ deletedIds: string[] }>('/tasks/deleted-since', { params: { since } }),
}

export const peopleApi = {
  getAll: (params?: Record<string, unknown>) => api.get('/people', { params }),
  getOne: (id: string) => api.get(`/people/${id}`),
  getWorkload: (id: string) => api.get(`/people/${id}/workload`),
  update: (id: string, data: Record<string, unknown>) => api.patch(`/people/${id}`, data),
  // Current user
  getMyProfile: () => api.get('/people/me/profile'),
  getMySettings: () => api.get('/people/me/settings'),
  updateMySettings: (settings: Record<string, unknown>) => api.patch('/people/me/settings', settings),
  // MCP API token
  getMcpTokenStatus: () => api.get('/people/me/mcp-token'),
  generateMcpToken: () => api.post('/people/me/mcp-token'),
  revokeMcpToken: () => api.delete('/people/me/mcp-token'),
  // Absences (отпуска, больничные и т.д.)
  getAbsences: (personId: string, params?: { year?: number; startDate?: string; endDate?: string }) =>
    api.get(`/people/${personId}/absences`, { params }),
  createAbsence: (personId: string, data: Record<string, unknown>) =>
    api.post(`/people/${personId}/absences`, data),
  updateAbsence: (absenceId: string, data: Record<string, unknown>) =>
    api.patch(`/people/absences/${absenceId}`, data),
  deleteAbsence: (absenceId: string) => api.delete(`/people/absences/${absenceId}`),
  getAllAbsencesForPeriod: (startDate: string, endDate: string, personIds?: string[]) =>
    api.get('/people/absences/period', {
      params: { startDate, endDate, personIds: personIds?.join(',') },
    }),
  // Commit statistics
  getCommitStats: (personId: string, params?: { startDate?: string; endDate?: string; limit?: number }) =>
    api.get<CommitStatsResult>(`/people/${personId}/commit-stats`, { params }),
  // Department memberships (superadmin only)
  getMemberships: (personId: string) =>
    api.get<DepartmentMembership[]>(`/people/${personId}/memberships`),
  addMembership: (personId: string, data: { departmentId: string; roles?: string[]; isPrimary?: boolean }) =>
    api.post<DepartmentMembership>(`/people/${personId}/memberships`, data),
  updateMembership: (personId: string, departmentId: string, data: { roles?: string[]; isPrimary?: boolean }) =>
    api.patch<DepartmentMembership>(`/people/${personId}/memberships/${departmentId}`, data),
  removeMembership: (personId: string, departmentId: string) =>
    api.delete(`/people/${personId}/memberships/${departmentId}`),
}

// ═══ Git-идентичности персон и автор коммита ═══

export type GitIdentityKind = 'person' | 'service' | 'external'

export interface GitIdentity {
  id: string
  personId: string | null
  email: string
  /** null — идентичность по одному email (любое имя) */
  name: string | null
  kind: GitIdentityKind
  displayName: string | null
  source: 'manual' | 'gitlab'
  createdBy: string | null
  createdAt: string
}

export type GitIdentitySuggestionReason =
  | 'name_matches_full_name'
  | 'name_matches_gitlab_username'
  | 'local_part_matches_gitlab_username'
  | 'local_part_matches_email'
  | 'gitlab_user_email'

export interface GitIdentitySuggestion {
  email: string
  name: string | null
  commitCount: number
  lastCommitAt: string | null
  reasons: GitIdentitySuggestionReason[]
}

export interface UnmatchedCommitAuthor {
  email: string
  name: string | null
  commitCount: number
  lastCommitAt: string | null
  projects: Array<{ id: string; title: string; slug: string | null }>
}

/** Подпись автора коммита (вычисляется на бэкенде) */
export interface CommitAuthorView {
  kind: 'person' | 'service' | 'external' | 'unknown'
  label: string
  personId: string | null
}

export const gitIdentitiesApi = {
  listForPerson: (personId: string) =>
    api.get<{ items: GitIdentity[]; canManage: boolean }>(`/people/${personId}/git-identities`),
  suggestions: (personId: string) =>
    api.get<GitIdentitySuggestion[]>(`/people/${personId}/git-identities/suggestions`),
  /** fromGitlab — адрес из подсказки GitLab; источник сервер проверяет сам */
  addToPerson: (personId: string, data: { email: string; name?: string | null; fromGitlab?: boolean }) =>
    api.post<GitIdentity>(`/people/${personId}/git-identities`, data),
  removeFromPerson: (personId: string, identityId: string) =>
    api.delete(`/people/${personId}/git-identities/${identityId}`),
  // Админские (admin+)
  unmatched: () => api.get<UnmatchedCommitAuthor[]>('/git-identities/unmatched'),
  listUnlinked: () => api.get<GitIdentity[]>('/git-identities/unlinked'),
  addUnlinked: (data: { email: string; name?: string | null; kind: 'service' | 'external'; displayName?: string | null }) =>
    api.post<GitIdentity>('/git-identities/unlinked', data),
  removeUnlinked: (identityId: string) => api.delete(`/git-identities/${identityId}`),
  recompute: () => api.post<{ updated: number }>('/git-identities/recompute'),
}

// Сборщик задач (только superadmin)
export type GroomerFindingState = 'proposed' | 'accepted' | 'rejected' | 'applied' | 'reverted'

export interface GroomerFinding {
  id: string
  runId: string
  taskId: string
  taskHumanId: string
  taskTitle: string
  finding: string
  action: string
  autoAllowed: boolean
  confidence: 'high' | 'medium' | 'low'
  target: string | null
  reason: string
  evidence: string[]
  state: GroomerFindingState
  decidedAt: string | null
  appliedAt: string | null
  previousValue: string | null
}

export interface GroomerRun {
  id: string
  mode: 'dry-run' | 'apply'
  trigger: 'cron' | 'manual'
  status: 'running' | 'done' | 'failed'
  startedAt: string
  finishedAt: string | null
  stats: {
    scanned: number
    protectedCount: number
    findings: number
    byFinding: Record<string, number>
    autoAllowed: number
  }
  digestMd?: string | null
  error?: string | null
}

export const taskGroomerApi = {
  config: () =>
    api.get<{ mode: string; autoActions: string[]; digestRecipients: number; enabled: boolean }>(
      '/task-groomer/config',
    ),
  runs: () => api.get<GroomerRun[]>('/task-groomer/runs'),
  run: (id: string) =>
    api.get<{ run: GroomerRun; findings: GroomerFinding[] }>(`/task-groomer/runs/${id}`),
  runNow: () => api.post<GroomerRun>('/task-groomer/runs'),
  accept: (id: string) => api.post<GroomerFinding>(`/task-groomer/findings/${id}/accept`),
  reject: (id: string) => api.post<GroomerFinding>(`/task-groomer/findings/${id}/reject`),
  revert: (id: string) => api.post<GroomerFinding>(`/task-groomer/findings/${id}/revert`),
}

// Type for department membership
export interface DepartmentMembership {
  id: string
  personId: string
  departmentId: string
  department?: {
    id: string
    name: string
  }
  roles: string[]
  isPrimary: boolean
  createdAt: string
}

// Types for commit stats
export interface CommitStatsResult {
  totalCommits: number
  totalLinesAdded: number
  totalLinesRemoved: number
  totalFilesChanged: number
  byProject: Array<{
    projectId: string
    projectTitle: string
    commits: number
    linesAdded: number
    linesRemoved: number
  }>
  byMonth: Array<{
    month: string
    commits: number
    linesAdded: number
    linesRemoved: number
  }>
  recentCommits: Array<{
    id: string
    shortSha: string
    title: string
    authoredDate: string
    linesAdded: number
    linesRemoved: number
    projectId: string
    projectTitle: string
  }>
}

export const departmentsApi = {
  getAll: (params?: Record<string, unknown>) => api.get('/departments', { params }),
  getOne: (id: string) => api.get(`/departments/${id}`),
  getTree: () => api.get('/departments/tree'),
}

export const commentsApi = {
  getByTask: (taskId: string) => api.get(`/tasks/${taskId}/comments`),
  create: (taskId: string, data: { content: string; parentCommentId?: string }) =>
    api.post(`/tasks/${taskId}/comments`, data),
  update: (id: string, data: { content: string }) => api.patch(`/comments/${id}`, data),
  delete: (id: string) => api.delete(`/comments/${id}`),
}

export interface AttachmentUploadStatus {
  uploadId: string
  partSize: number
  partsTotal: number
  size: number
  uploadedParts: number[]
  /** SHA-256 полученных частей — сверка при докачке */
  partSha256?: Record<string, string>
  /** Загрузка уже завершена этим вложением */
  attachmentId?: string
}

export const attachmentsApi = {
  getByTask: (taskId: string) => api.get(`/tasks/${taskId}/attachments`),
  upload: (taskId: string, file: File, options?: { inline?: boolean }) => {
    const formData = new FormData()
    formData.append('file', file)
    const params = options?.inline ? '?inline=true' : ''
    return api.post(`/tasks/${taskId}/attachments${params}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  // Загрузка частями (до 1 ГБ) с докачкой — см. composables/useAttachmentUpload
  startUpload: (
    taskId: string,
    body: { filename: string; size: number; mimeType?: string; inline?: boolean },
  ) => api.post<AttachmentUploadStatus>(`/tasks/${taskId}/attachment-uploads`, body),
  uploadStatus: (taskId: string, uploadId: string) =>
    api.get<AttachmentUploadStatus>(`/tasks/${taskId}/attachment-uploads/${uploadId}`),
  uploadPart: (
    taskId: string,
    uploadId: string,
    partNumber: number,
    data: ArrayBuffer,
    sha256: string,
    onProgress?: (loaded: number) => void,
    signal?: AbortSignal,
  ) =>
    api.put(`/tasks/${taskId}/attachment-uploads/${uploadId}/parts/${partNumber}`, data, {
      headers: { 'Content-Type': 'application/octet-stream', 'x-content-sha256': sha256 },
      // Таймаут по простою задаёт вызывающий через signal: медленный канал не обрываем
      timeout: 0,
      signal,
      onUploadProgress: onProgress ? (e) => onProgress(e.loaded) : undefined,
    }),
  completeUpload: (taskId: string, uploadId: string) =>
    api.post(`/tasks/${taskId}/attachment-uploads/${uploadId}/complete`, undefined, {
      timeout: 120000,
    }),
  cancelUpload: (taskId: string, uploadId: string) =>
    api.delete(`/tasks/${taskId}/attachment-uploads/${uploadId}`),
  cancelAllMyUploads: () => api.delete<{ cancelled: number }>('/attachment-uploads/mine'),
  getOne: (id: string) => api.get(`/attachments/${id}`),
  getDownloadUrl: (id: string) => api.get(`/attachments/${id}/url`),
  delete: (id: string) => api.delete(`/attachments/${id}`),
}

export const taskLinksApi = {
  getByTask: (taskId: string) => api.get(`/tasks/${taskId}/links`),
  create: (taskId: string, data: { targetTaskId: string; linkType: string; relation?: string }) =>
    api.post(`/tasks/${taskId}/links`, data),
  delete: (id: string) => api.delete(`/task-links/${id}`),
}

export const historyApi = {
  getByTask: (taskId: string) => api.get(`/tasks/${taskId}/history`),
}

export const aiApi = {
  health: () => api.get('/ai/health'),
  models: () => api.get('/ai/models'),
  chat: (data: {
    messages: Array<{ role: 'system' | 'user' | 'assistant'; content: string }>;
    model?: string;
    temperature?: number;
    context?: string;
    conversationId?: string;
  }) => api.post('/ai/chat', data),
  generate: (data: { prompt: string; model?: string; temperature?: number; maxTokens?: number; system?: string }) =>
    api.post('/ai/generate', data),
  summarize: (data: { text: string; maxLength?: number }) => api.post('/ai/summarize', data),
  generateTitle: (data: { description?: string; tags?: string[] }) =>
    api.post('/ai/generate-title', data),
  analyzeTask: (data: { title: string; description?: string; tags?: string[] }) =>
    api.post('/ai/analyze-task', data),
  getStats: (days?: number) => api.get('/ai/stats', { params: { days } }),
  getDailyStats: (days?: number) => api.get('/ai/stats/daily', { params: { days } }),
  // Conversations
  createConversation: () => api.post('/ai/conversations'),
  getConversations: () => api.get('/ai/conversations'),
  getConversation: (id: string) => api.get(`/ai/conversations/${id}`),
  // Task context & AI improvements
  improveText: (data: { text: string; mode?: 'fix_errors' | 'structure' | 'clarify' | 'all'; taskContext?: string }) =>
    api.post('/ai/improve-text', data),
  generateTaskAnnotation: (taskId: string, forceRegenerate = false) =>
    api.post('/ai/task-annotation', { taskId, forceRegenerate }),
  getTaskAnnotation: (taskId: string) => api.get(`/ai/annotation/${taskId}`),
  getTaskContext: (taskId: string) => api.get(`/ai/task-context/${taskId}`),
  estimateTime: (data: { taskId: string; description?: string }) => api.post('/ai/estimate-time', data),
  // Annotation queue
  getQueueStats: () => api.get('/ai/annotations/queue-stats'),
  regenerateAll: () => api.post('/ai/annotations/regenerate-all'),
  updateAll: () => api.post('/ai/annotations/update-all'),
  regenerateMissing: () => api.post('/ai/annotations/regenerate-missing'),
  queueTaskAnnotation: (taskId: string) => api.post(`/ai/annotations/queue-task/${taskId}`),
  getAnnotationStats: (days?: number) => api.get('/ai/annotations/stats', { params: { days } }),
  getGenerationStatus: (taskId: string) => api.get(`/ai/annotations/generation-status/${taskId}`),
  stopAnnotations: () => api.post('/ai/annotations/stop'),
  cancelTaskAnnotation: (taskId: string) => api.post(`/ai/annotations/cancel/${taskId}`),
  clearQueueStats: () => api.post('/ai/annotations/clear-stats'),
  clearAiStats: () => api.post('/ai/stats/clear'),
  // Job management (PostgreSQL-based, single source of truth)
  getJobStats: (jobType?: 'annotation' | 'embedding') =>
    api.get('/ai/jobs/stats', { params: jobType ? { jobType } : {} }),
  getJobs: (params?: { status?: string; jobType?: string; entityType?: string; limit?: number }) =>
    api.get('/ai/jobs', { params }),
  getActiveJobs: () => api.get('/ai/jobs/active'),
  getJob: (id: string) => api.get(`/ai/jobs/${id}`),
  getEntityJob: (entityType: string, entityId: string) =>
    api.get(`/ai/jobs/entity/${entityType}/${entityId}`),
  cancelJob: (id: string) => api.post(`/ai/jobs/${id}/cancel`),
  cancelAllJobs: () => api.post('/ai/jobs/cancel-all'),
  // Provider management (superadmin only)
  getProviders: () => api.get('/ai/providers'),
  getProvider: (id: string) => api.get(`/ai/providers/${id}`),
  createProvider: (data: AiProviderInput) => api.post('/ai/providers', data),
  updateProvider: (id: string, data: Partial<AiProviderInput>) => api.put(`/ai/providers/${id}`, data),
  deleteProvider: (id: string) => api.delete(`/ai/providers/${id}`),
  toggleProvider: (id: string) => api.post(`/ai/providers/${id}/toggle`),
  checkProviderHealth: (id: string) => api.post(`/ai/providers/health-check/${id}`),
  fetchProviderModels: (id: string) => api.post(`/ai/providers/${id}/fetch-models`),
  // Model config management (superadmin only)
  getModelConfigs: (category?: AiModelCategory) => api.get('/ai/model-configs', { params: category ? { category } : {} }),
  getModelConfigMatrix: () => api.get('/ai/model-configs/matrix'),
  createModelConfig: (data: AiModelConfigInput) => api.post('/ai/model-configs', data),
  updateModelConfig: (id: string, data: Partial<AiModelConfigInput>) => api.put(`/ai/model-configs/${id}`, data),
  deleteModelConfig: (id: string) => api.delete(`/ai/model-configs/${id}`),
  toggleModelConfig: (id: string) => api.post(`/ai/model-configs/${id}/toggle`),
  reorderModelConfigs: (category: AiModelCategory, ids: string[]) => api.post('/ai/model-configs/reorder', { category, ids }),
  // System prompts management (superadmin only)
  getSystemPrompts: () => api.get('/ai/system-prompts'),
  getSystemPrompt: (id: string) => api.get(`/ai/system-prompts/${id}`),
  updateSystemPrompt: (id: string, data: SystemPromptInput) => api.put(`/ai/system-prompts/${id}`, data),
  toggleSystemPrompt: (id: string) => api.post(`/ai/system-prompts/${id}/toggle`),
  resetSystemPrompt: (key: string) => api.post(`/ai/system-prompts/${key}/reset`),
  createSystemPrompt: (data: SystemPromptInput) => api.post('/ai/system-prompts', data),
  deleteSystemPrompt: (id: string) => api.delete(`/ai/system-prompts/${id}`),
  // Embedding queue management
  getEmbeddingQueueStats: () => api.get('/ai/embeddings/queue/stats'),
  queueAllEmbeddings: () => api.post('/ai/embeddings/queue/all'),
  queueTaskEmbeddings: () => api.post('/ai/embeddings/queue/tasks'),
  queueArticleEmbeddings: () => api.post('/ai/embeddings/queue/articles'),
  queueSingleEmbedding: (type: 'task' | 'article', entityId: string) =>
    api.post('/ai/embeddings/queue/single', { type, entityId }),
  stopEmbeddingQueue: () => api.post('/ai/embeddings/queue/stop'),
  clearEmbeddingQueueStats: () => api.post('/ai/embeddings/queue/clear-stats'),
  regenerateAllEmbeddings: () => api.post('/ai/embeddings/queue/regenerate'),
  // RAG search
  ragSearch: (query: string, options?: { limit?: number; includeTasks?: boolean; includeArticles?: boolean }) =>
    api.post('/ai/rag/search', { query, ...options }),
  ragStats: () => api.get('/ai/rag/stats'),
  // Semantic search with filters and pagination
  semanticSearch: (params: SemanticSearchParams, config?: { signal?: AbortSignal }) =>
    api.post('/ai/search/semantic', params, config),
  // AI Report Generation
  getReportTypes: () => api.get('/ai/reports/types'),
  generateReport: (params: GenerateReportParams) =>
    api.post('/ai/reports/generate', params),
  // Full-text search (PostgreSQL tsvector)
  fullTextSearch: (params: FullTextSearchParams, config?: { signal?: AbortSignal }) =>
    api.post('/ai/search/fulltext', params, config),
  getSearchSuggestions: (prefix: string, limit = 10) =>
    api.get<{ suggestions: string[] }>('/ai/search/suggestions', { params: { prefix, limit } }),
  // Failed messages (DLQ) management
  getFailedMessages: (limit = 50) =>
    api.get<FailedMessagesResponse>('/ai/annotations/failed', { params: { limit } }),
  getDLQStats: () => api.get<{ count: number }>('/ai/annotations/failed/stats'),
  retryFailedMessage: (taskId: string) =>
    api.post<{ retried: boolean; jobId?: string; message: string }>(`/ai/annotations/failed/${taskId}/retry`),
  retryAllFailedMessages: () =>
    api.post<{ retried: number; failed: number; message: string }>('/ai/annotations/failed/retry-all'),
  deleteFailedMessage: (taskId: string) =>
    api.delete<{ deleted: boolean; message: string }>(`/ai/annotations/failed/${taskId}`),
  purgeFailedMessages: () =>
    api.delete<{ purged: number; message: string }>('/ai/annotations/failed'),
  // Model/Provider temporary unavailability management
  getAvailabilityStatus: () => api.get<ModelAvailabilityStatus>('/ai/availability'),
  clearAllAvailability: () => api.post<{ success: boolean; message: string }>('/ai/availability/clear'),
  markModelUnavailable: (modelId: string, data?: { reason?: string; ttlMinutes?: number }) =>
    api.post<{ success: boolean; message: string }>(`/ai/availability/model/${modelId}/mark`, data || {}),
  clearModelAvailability: (modelId: string) =>
    api.post<{ success: boolean; message: string }>(`/ai/availability/model/${modelId}/clear`),
  markProviderUnavailable: (providerId: string, data?: { reason?: string; ttlMinutes?: number }) =>
    api.post<{ success: boolean; message: string }>(`/ai/availability/provider/${providerId}/mark`, data || {}),
  clearProviderAvailability: (providerId: string) =>
    api.post<{ success: boolean; message: string }>(`/ai/availability/provider/${providerId}/clear`),
}

// Прогноз трудоёмкости и подсказка исполнителей по истории подразделения
export type ForecastLevel = 'person_work_type' | 'work_type' | 'department'

export interface ForecastAnalog {
  id: string
  humanId: string | null
  title: string
  minutes: number
}

export interface EffortForecastPart {
  departmentId: string
  departmentName: string
  /** Статистика чужого подразделения смотрящему не показывается */
  hidden: boolean
  level: ForecastLevel | null
  sampleSize: number
  p25: number | null
  p50: number | null
  p75: number | null
  analogs: ForecastAnalog[]
}

export interface EffortForecast {
  taskId: string
  workType: string | null
  total: { p25: number | null; p50: number | null; p75: number | null }
  complete: boolean
  insufficientHistory: boolean
  /** Все части — чужие подразделения: история есть, но не видна */
  allHidden: boolean
  parts: EffortForecastPart[]
  /** Зонт: прогноз — сумма по видимым потомкам (закрытые — факт, открытые — вилка) + собственная часть */
  umbrella?: EffortForecastUmbrella
}

export interface EffortForecastUmbrella {
  /** Открытых потомков, для которых нужна оценка */
  childCount: number
  /** Из них получили оценку */
  forecasted: number
  /** Закрытых и постоянных потомков (берётся факт) */
  closedCount: number
  closedMinutes: number
  /** Собственное время зонта */
  ownMinutes: number
  /** Собственная часть зонта вошла в сумму */
  ownIncluded: boolean
  /** Открытых потомков больше лимита: сверх него учтён только факт */
  truncated: boolean
}

export interface AssigneeCandidate {
  personId: string
  name: string
  similarCount: number
  similarTasks: Array<{ id: string; humanId: string | null; title: string }>
  openTasks: number
  absence: { from: string; to: string } | null
}

export interface AssigneeGroup {
  departmentId: string
  departmentName: string
  basis: 'similar' | 'work_type' | 'department'
  candidates: AssigneeCandidate[]
}

export interface ForecastCalibration {
  departmentId: string | null
  n: number
  inRange: number | null
  withinTwice: number | null
  geoError: number | null
}

export const forecastApi = {
  effort: (taskId: string) => api.get<EffortForecast>(`/forecast/tasks/${taskId}/effort`),
  assignees: (taskId: string) =>
    api.get<{ taskId: string; groups: AssigneeGroup[] }>(`/forecast/tasks/${taskId}/assignees`),
  calibration: (departmentId?: string) =>
    api.get<ForecastCalibration>('/forecast/calibration', {
      params: departmentId ? { departmentId } : undefined,
    }),
}

export interface SemanticSearchParams {
  query: string
  limit?: number
  offset?: number
  includeArticles?: boolean
  includeTasks?: boolean
  minSimilarity?: number
  taskStatus?: string[]
  tags?: string[]
  createdAfter?: string
  createdBefore?: string
  personId?: string
  priorityMin?: number
  priorityMax?: number
  articleStatus?: string[]
  // false — не подменять выдачу полнотекстовой, если модель эмбеддингов
  // недоступна (вернётся 503/504). Нужен там, где важен процент сходства.
  allowFallback?: boolean
}

export interface SemanticSearchResult {
  query: string
  // 'fulltext' — модель эмбеддингов недоступна, выдача из полнотекстового поиска
  mode?: 'semantic' | 'fulltext'
  fallbackReason?: 'no_embedding_model' | 'timeout'
  items: SemanticSearchItem[]
  pagination: {
    limit: number
    offset: number
    total: number
    hasMore: boolean
  }
  stats: {
    queryEmbeddingTime: number
    searchTime: number
    totalTokensUsed: number
  }
}

export interface SemanticSearchItem {
  type: 'task' | 'article'
  id: string
  title: string
  content: string
  // null — в полнотекстовом режиме сходство не измеряется
  similarity: number | null
  metadata?: {
    status?: string
    category?: string
    tags?: string[]
    priority?: number
    humanId?: string
    externalId?: string
    assigneeName?: string
    createdAt?: string
    updatedAt?: string
  }
}

// Full-Text Search Types (PostgreSQL tsvector)
export interface FullTextSearchParams {
  query: string
  limit?: number
  offset?: number
  includeArticles?: boolean
  includeTasks?: boolean
  taskStatus?: string[]
  taskPriority?: string[]
  personId?: string
  tags?: string[]
  articleStatus?: string[]
  category?: string
  dateFrom?: string
  dateTo?: string
}

export interface FullTextSearchResult {
  id: string
  type: 'task' | 'article'
  title: string
  excerpt: string
  rank: number
  highlights: string[]
  metadata: {
    humanId?: string
    externalId?: string
    status?: string
    priority?: string
    tags?: string[]
    slug?: string
    category?: string
    articleStatus?: string
    createdAt: string
    updatedAt: string
  }
}

export interface FullTextSearchResponse {
  query: string
  results: FullTextSearchResult[]
  total: number
  searchTimeMs: number
  filters: {
    includeTasks: boolean
    includeArticles: boolean
    taskStatus?: string[]
    taskPriority?: string[]
    articleStatus?: string[]
    category?: string
    tags?: string[]
  }
}

// Model/Provider Availability Types
export interface ModelUnavailabilityInfo {
  modelId: string
  providerId: string
  providerName: string
  reason: string
  markedAt: string // ISO timestamp
  expiresAt: string // ISO timestamp
  failureCount: number
  lastError?: string
}

export interface ProviderUnavailabilityInfo {
  providerId: string
  providerName: string
  reason: string
  markedAt: string
  expiresAt: string
  failureCount: number
  lastError?: string
}

export interface ModelAvailabilityStatus {
  models: ModelUnavailabilityInfo[]
  providers: ProviderUnavailabilityInfo[]
  summary: {
    unavailableModels: number
    unavailableProviders: number
  }
}

// Failed Messages (DLQ) Types
export interface FailedMessage {
  taskId: string
  jobId?: string
  attempt: number
  maxAttempts: number
  firstQueuedAt: number
  lastAttemptAt?: number
  failedAt: number
  error?: string
}

export interface FailedMessagesResponse {
  messages: FailedMessage[]
  total: number
}

// AI Report Generation Types
export type ReportType = 'summary' | 'detailed' | 'status' | 'weekly' | 'comparison'

export interface ReportTypeInfo {
  type: ReportType
  label: string
  description: string
}

export interface GenerateReportParams {
  type: ReportType
  taskIds: string[]
  title?: string
  includeComments?: boolean
  includeAnnotations?: boolean
  customInstructions?: string
}

export interface GeneratedReport {
  title: string
  type: ReportType
  content: string
  taskCount: number
  taskIds: string[]
  generatedAt: string
  model: string
  provider: string
  processingTimeMs: number
  totalTokens: number
}

export interface GenerateReportResponse {
  success?: boolean
  report?: GeneratedReport
  error?: boolean
  message?: string
}

export type AiModelType = 'chat' | 'embedding' | 'both'

export interface AiProviderModel {
  id: string
  name: string
  priority: number
  enabled: boolean
  maxTokens?: number
  contextLength?: number
  isEmbedding?: boolean // Deprecated - use modelType
  modelType?: AiModelType
  embeddingDimensions?: number
}

export interface AiProvider {
  id: string
  name: string
  type: 'ollama' | 'yandexgpt' | 'gigachat' | 'openai'
  baseUrl: string
  enabled: boolean
  maxConcurrent: number
  description?: string
  models: AiProviderModel[]
  // Ключ наружу не отдаётся — только признак, задан ли он
  hasApiKey: boolean
  folderId?: string
  isHealthy: boolean
  lastHealthCheck?: string
  healthError?: string
  createdAt: string
  updatedAt: string
}

export interface AiProviderInput {
  name: string
  type: 'ollama' | 'yandexgpt' | 'gigachat' | 'openai'
  baseUrl: string
  enabled?: boolean
  maxConcurrent?: number
  description?: string
  models?: AiProviderModel[]
  // Пусто/не передан — ключ не меняется; null — очистить
  apiKey?: string | null
  folderId?: string
}

export type AiModelCategory = 'queue' | 'admin' | 'edit' | 'view' | 'embedding' | 'telegram' | 'analytics'

export interface AiModelConfig {
  id: string
  providerId: string
  provider?: AiProvider
  modelId: string
  category: AiModelCategory
  priority: number
  enabled: boolean
  createdAt: string
  updatedAt: string
}

export interface AiModelConfigInput {
  providerId: string
  modelId: string
  category: AiModelCategory
  priority?: number
  enabled?: boolean
}

// System Prompts
export type SystemPromptType = 'task_annotation' | 'text_improvement' | 'time_estimation' | 'chat_system' | 'custom'

export interface SystemPromptVariable {
  name: string
  description: string
  required: boolean
}

export interface SystemPrompt {
  id: string
  key: string
  type: SystemPromptType
  name: string
  description: string | null
  prompt: string
  enabled: boolean
  variables: SystemPromptVariable[]
  version: number
  createdAt: string
  updatedAt: string
}

export interface SystemPromptInput {
  key?: string
  type?: SystemPromptType
  name?: string
  description?: string
  prompt?: string
  enabled?: boolean
  variables?: SystemPromptVariable[]
}

// Knowledge Base Types
export type ArticleStatus = 'draft' | 'published' | 'archived'

export interface KnowledgeFolder {
  id: string
  name: string
  slug: string
  parentId: string | null
  /** Область папки: null — общая, иначе подразделение */
  departmentId?: string | null
  description: string | null
  icon: string
  sortOrder: number
  children?: KnowledgeFolderTree[]
  articleCount?: number
  createdAt: string
  updatedAt: string
}

export interface KnowledgeFolderTree extends KnowledgeFolder {
  children: KnowledgeFolderTree[]
  articleCount?: number
}

export interface CreateFolderInput {
  name: string
  slug?: string
  parentId?: string
  /** Подразделение папки; не задано — основное подразделение пользователя */
  departmentId?: string | null
  description?: string
  icon?: string
  sortOrder?: number
}

export interface UpdateFolderInput {
  name?: string
  slug?: string
  parentId?: string
  description?: string
  icon?: string
  sortOrder?: number
}

export interface KnowledgeArticle {
  id: string
  title: string
  slug: string | null
  content: string | null
  excerpt: string | null
  category: string | null
  folderId: string | null
  folder?: KnowledgeFolder | null
  /** Проект статьи — её аудитория (изоляция подразделений) */
  sourceProjectId?: string | null
  tags: string[]
  status: ArticleStatus
  authorId: string | null
  author?: { id: string; fullName: string } | null
  lastEditorId: string | null
  lastEditor?: { id: string; fullName: string } | null
  publishedAt: string | null
  viewCount: number
  createdAt: string
  updatedAt: string
  // Published content snapshot
  publishedTitle?: string | null
  publishedContent?: string | null
  publishedExcerpt?: string | null
  // RAG and versioning status
  hasEmbedding?: boolean
  hasUnpublishedChanges?: boolean
}

export interface CreateArticleInput {
  title: string
  slug?: string
  content?: string
  excerpt?: string
  category?: string
  folderId?: string
  sourceProjectId?: string | null
  tags?: string[]
  status?: ArticleStatus
}

export interface UpdateArticleInput {
  title?: string
  slug?: string
  content?: string
  excerpt?: string
  category?: string
  folderId?: string
  sourceProjectId?: string | null
  tags?: string[]
  status?: ArticleStatus
}

export interface ArticleFilters {
  status?: ArticleStatus
  category?: string
  folderId?: string
  tags?: string[]
  search?: string
  limit?: number
  offset?: number
}

// Dashboard Types

// Extracted types for reuse in components
export interface SystemHealth {
  database: 'up' | 'down' | 'unknown'
  redis: 'up' | 'down' | 'unknown'
  rabbitmq: 'up' | 'down' | 'unknown'
  storage: 'up' | 'down' | 'unknown'
}

export interface PostgresStats {
  connections: {
    current: number
    max: number
    usagePercent: number
    byState: Record<string, number>
  }
  database: {
    name: string
    sizeMb: number
    tablesCount: number
  }
  performance: {
    cacheHitRatio: number
    avgQueryTimeMs: number
    activeQueries: number
  }
}

export interface DashboardAiProvider {
  id: string
  name: string
  type: string
  status: 'online' | 'offline' | 'error'
  priority: number
  models?: string[]
  lastError?: string
}

export interface DashboardData {
  counts: {
    activeTasks: number
    completedTasks: number
    totalTasks: number
    people: number
    systems: number
  }
  aiStats: {
    totalConversations: number
    totalMessages: number
    totalTokens: number
    byModel: Array<{
      model: string
      provider: string
      requestCount: number
      totalTokens: number
      avgProcessingTimeMs: number
    }>
  } | null
  aiDailyStats: Array<{
    date: string
    model: string
    requestCount: number
    totalTokens: number
    avgProcessingTimeMs: number
    errorCount: number
  }>
  queueStats: {
    waiting: number
    active: number
    completed: number
    failed: number
    total: number
    avgProcessingTimeMs: number
  } | null
  annotationStats: {
    total: number
    bySource: Array<{
      source: string
      count: number
      totalTokens: number
      avgProcessingTimeMs: number
      minProcessingTimeMs: number
      maxProcessingTimeMs: number
      totalProcessingTimeMs: number
    }>
    byModel: Array<{
      model: string
      count: number
      totalTokens: number
      avgProcessingTimeMs: number
    }>
    bySourceAndModel: Array<{
      source: string
      model: string
      count: number
      totalTokens: number
      avgProcessingTimeMs: number
    }>
    totalTokens: number
    avgProcessingTimeMs: number
    tasksWithAnnotations: number
    tasksWithoutAnnotations: number
    missingBreakdown?: {
      active: number
      drafts: number
      deleted: number
    }
  } | null
  embeddingStats: {
    tasks: {
      total: number
      withEmbeddings: number
      withoutEmbeddings: number
      missingBreakdown?: {
        active: number   // Real tasks without embeddings
        drafts: number   // Draft tasks (excluded from indexing)
        deleted: number  // Deleted tasks (excluded from indexing)
      }
      byProvider: Record<string, number>
      byModel: Record<string, number>
      averageProcessingTimeMs: number
    }
    articles: {
      total: number
      withEmbeddings: number
      withoutEmbeddings: number
      byProvider: Record<string, number>
      byModel: Record<string, number>
      averageProcessingTimeMs: number
    }
    queue: {
      waiting: number
      active: number
      completed: number
      failed: number
    }
  } | null
  postgresStats: {
    connections: {
      current: number
      max: number
      usagePercent: number
      byState: Record<string, number>
    }
    database: {
      name: string
      sizeMb: number
      tablesCount: number
    }
    performance: {
      cacheHitRatio: number
      avgQueryTimeMs: number
      activeQueries: number
    }
  } | null
  systemHealth: {
    database: 'up' | 'down' | 'unknown'
    redis: 'up' | 'down' | 'unknown'
    rabbitmq: 'up' | 'down' | 'unknown'
    storage: 'up' | 'down' | 'unknown'
  }
  aiProviders: Array<{
    id: string
    name: string
    type: string
    status: 'online' | 'offline' | 'error'
    priority: number
    models?: string[]
    lastError?: string
  }>
  // Только для admin+: остальным сервер журнал ошибок не отдаёт
  recentErrors?: Array<{
    id: string
    timestamp: string
    type: string
    message: string
    taskId?: string
  }>
  meta: {
    cachedAt: string
    refreshedAt: string | null
    cacheAge: number
  }
}

export interface DashboardError {
  id: string
  timestamp: string
  type: string
  message: string
  taskId?: string
}

// Business Stats Types
export interface BusinessStatsFilters {
  personId?: string // Deprecated: use personIds instead
  personIds?: string[]
  ownerIds?: string[] // Strict owner filter (no unassigned fallback)
  projectId?: string // Deprecated: use projectIds instead
  projectIds?: string[]
  status?: string[]
  priority?: string[]
  tags?: string[]
  // Excluded items (per-item granularity)
  excludedTags?: string[]
  excludedOwnerIds?: string[]
  excludedProjectIds?: string[]
  excludedPersonIds?: string[]
}

export interface WeekStats {
  weekStart: string
  weekEnd: string
  weekLabel: string
  planned: number
  completed: number
  created: number
  overdue: number
}

export interface TaskSummary {
  id: string
  humanId?: string
  externalId: string
  title: string
  status: string
  priority: string
  progress: number
  plannedDate: string | null
  hardDeadline: string | null
  assigneeName: string | null
  tags: string[]
}

export type AttentionCategory = 'overdue' | 'dueThisWeek' | 'dueNextWeek' | 'highPriority' | 'recentlyUpdated'

export interface AttentionCounts {
  overdue: number
  dueThisWeek: number
  dueNextWeek: number
  highPriority: number
  recentlyUpdated: number
}

export interface BusinessStats {
  summary: {
    total: number
    byStatus: Record<string, number>
    byPriority: Record<string, number>
  }
  weeklyStats: {
    previous: WeekStats
    current: WeekStats
    next: WeekStats
  }
  attention: {
    counts: AttentionCounts
    // Deprecated: backward compatibility, always empty
    overdue: TaskSummary[]
    dueThisWeek: TaskSummary[]
    dueNextWeek: TaskSummary[]
    highPriority: TaskSummary[]
    recentlyUpdated: TaskSummary[]
  }
  filters: BusinessStatsFilters
  archivedCount?: number
  appliedFilters: {
    personName?: string // Deprecated: use personNames instead
    personNames?: string[]
    projectName?: string
  }
}

export const dashboardApi = {
  // Get dashboard data (cached + async refresh via WebSocket)
  getData: (refresh = false) =>
    api.get<{ data: DashboardData | null; cached: boolean; refreshing: boolean }>('/dashboard', {
      params: refresh ? { refresh: 'true' } : {},
    }),
  // Force synchronous refresh
  forceRefresh: () => api.post<DashboardData>('/dashboard/refresh'),
  // Get recent errors
  getErrors: () => api.get<DashboardError[]>('/dashboard/errors'),
  // Clear errors
  clearErrors: () => api.delete('/dashboard/errors'),
  // Get business stats with filters (counts only, no task lists)
  getBusinessStats: (filters?: BusinessStatsFilters) =>
    api.get<BusinessStats>('/dashboard/business-stats', { params: filters }),
}

// Project Types
export type ProjectStatus = 'active' | 'on_hold' | 'completed' | 'archived'
export type ProjectMemberRole = 'owner' | 'manager' | 'member' | 'viewer'

export interface ProjectMember {
  id: string
  projectId: string
  personId: string
  person?: {
    id: string
    fullName: string
    shortName?: string
    email: string
    avatarUrl?: string
  }
  role: ProjectMemberRole
  notes: string | null
  createdAt: string
}

export interface PublicSubmitConfig {
  welcomeText?: string
  allowedTags?: string[]
  requirePhone?: boolean
  allowAttachments?: boolean
  maxAttachments?: number
}

export interface ProjectSettings {
  publicSubmitEnabled?: boolean
  publicSubmitConfig?: PublicSubmitConfig
  [key: string]: unknown
}

export interface Project {
  id: string
  humanId?: string
  title: string
  slug: string
  description: string | null
  status: ProjectStatus
  priority: string
  readmeContent: string | null
  techSpecContent: string | null
  autoSummary: string | null
  autoSummaryUpdatedAt: string | null
  startDate: string | null
  targetDate: string | null
  gitlabRepoUrl: string | null
  gitlabProjectId: number | null
  gitlabSyncEnabled: boolean
  totalTasks: number
  completedTasks: number
  inProgressTasks: number
  progress: number
  tags: string[]
  settings: ProjectSettings
  metadata: Record<string, unknown>
  knowledgeFolderId: string | null
  ownerDepartmentId: string | null
  createdById: string | null
  createdBy?: {
    id: string
    fullName: string
  }
  ownerDepartment?: {
    id: string
    name: string
  }
  members?: ProjectMember[]
  createdAt: string
  updatedAt: string
}

// GitLab Commit types
export type CommitClassificationStatus = 'pending' | 'analyzing' | 'classified' | 'unclassified' | 'error'

export interface LinkedTaskInfo {
  id: string
  humanId: string | null
  title: string
}

export interface GitLabCommit {
  id: string
  projectId: string
  commitSha: string
  shortSha: string
  title: string | null
  message: string | null
  authorName: string | null
  authorEmail: string | null
  authorPersonId?: string | null
  /** Подпись автора: ФИО / внешний / служебный / сырое имя */
  author?: CommitAuthorView
  authoredDate: string | null
  webUrl: string | null
  diffSummary: string | null
  filesChanged: string[]
  linesAdded: number
  linesRemoved: number
  linkedTaskIds: string[]
  linkedTasks?: LinkedTaskInfo[]
  classificationStatus: CommitClassificationStatus
  classificationConfidence: number | null
  classificationReasoning: string | null
  isTechnical: boolean
  analyzedAt: string | null
  analyzedByModel: string | null
  processingTimeMs: number | null
  errorMessage: string | null
  createdAt: string
  updatedAt: string
}

export interface CommitStats {
  total: number
  pending: number
  classified: number
  unclassified: number
  technical: number
  error: number
}

export interface CreateProjectInput {
  title: string
  slug: string
  description?: string
  status?: ProjectStatus
  priority?: string
  readmeContent?: string
  techSpecContent?: string
  startDate?: string
  targetDate?: string
  gitlabRepoUrl?: string
  gitlabProjectId?: number
  gitlabAccessToken?: string
  gitlabSyncEnabled?: boolean
  tags?: string[]
  knowledgeFolderId?: string
  ownerDepartmentId?: string
  settings?: ProjectSettings
}

export interface UpdateProjectInput extends Partial<CreateProjectInput> {}

export interface ProjectFilters {
  status?: ProjectStatus
  search?: string
  departmentId?: string
  createdById?: string
  tag?: string
  includeArchived?: boolean
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: 'ASC' | 'DESC'
}

export interface AddProjectMemberInput {
  personId: string
  role?: ProjectMemberRole
  notes?: string
}

export interface UpdateProjectMemberInput {
  role?: ProjectMemberRole
  notes?: string
}

export const projectsApi = {
  // CRUD
  getAll: (params?: ProjectFilters) => api.get<{ data: Project[]; total: number; page: number; limit: number }>('/projects', { params }),
  getOne: (id: string) => api.get<Project>(`/projects/${id}`),
  getBySlug: (slug: string) => api.get<Project>(`/projects/by-slug/${slug}`),
  getByHumanId: (humanId: string) => api.get<Project>(`/projects/by-human-id/${humanId}`),
  create: (data: CreateProjectInput) => api.post<Project>('/projects', data),
  update: (id: string, data: UpdateProjectInput) => api.patch<Project>(`/projects/${id}`, data),
  delete: (id: string) => api.delete<Project>(`/projects/${id}`),
  getTags: () => api.get<string[]>('/projects/tags'),

  // Members
  getMembers: (projectId: string) => api.get<ProjectMember[]>(`/projects/${projectId}/members`),
  addMember: (projectId: string, data: AddProjectMemberInput) => api.post<ProjectMember>(`/projects/${projectId}/members`, data),
  updateMember: (projectId: string, personId: string, data: UpdateProjectMemberInput) =>
    api.patch<ProjectMember>(`/projects/${projectId}/members/${personId}`, data),
  removeMember: (projectId: string, personId: string) => api.delete(`/projects/${projectId}/members/${personId}`),
  getTimeByPerson: (projectId: string) => api.get<{ personId: string; fullName: string; totalMinutes: number }[]>(`/projects/${projectId}/time-by-person`),

  // Cross-Department Sharing
  getShares: (projectId: string) => api.get(`/projects/${projectId}/shares`),
  share: (projectId: string, data: { departmentId: string; accessLevel?: string; taskScope?: string }) => api.post(`/projects/${projectId}/shares`, data),
  updateShare: (projectId: string, departmentId: string, data: { accessLevel?: string; taskScope?: string }) => api.patch(`/projects/${projectId}/shares/${departmentId}`, data),
  getShareImpact: (projectId: string, departmentId: string) => api.get(`/projects/${projectId}/shares/${departmentId}/impact`),
  unshare: (projectId: string, departmentId: string, removeAssignments = false) =>
    api.delete(`/projects/${projectId}/shares/${departmentId}`, { params: removeAssignments ? { removeAssignments: 'true' } : {} }),

  // Tasks
  getTasks: (projectId: string) => api.get(`/projects/${projectId}/tasks`),
  addTask: (projectId: string, taskId: string) => api.post(`/projects/${projectId}/tasks/${taskId}`),
  removeTask: (projectId: string, taskId: string) => api.delete(`/projects/${projectId}/tasks/${taskId}`),

  // Documentation
  getReadme: (projectId: string) => api.get<{ content: string | null }>(`/projects/${projectId}/readme`),
  updateReadme: (projectId: string, content: string) => api.patch<Project>(`/projects/${projectId}/readme`, { content }),
  getTechSpec: (projectId: string) => api.get<{ content: string | null }>(`/projects/${projectId}/tech-spec`),
  updateTechSpec: (projectId: string, content: string) => api.patch<Project>(`/projects/${projectId}/tech-spec`, { content }),

  // AI
  generateSummary: (projectId: string) => api.post(`/projects/${projectId}/generate-summary`),
  updateDocs: (projectId: string, section?: 'readme' | 'techSpec') => api.post(`/projects/${projectId}/update-docs`, { section }),

  // GitLab
  testGitlabConnection: (projectId: string) => api.post<{ success: boolean; message: string; projectInfo?: unknown }>(`/projects/${projectId}/gitlab/test`),
  syncGitlab: (projectId: string, since?: string) => api.post<{ synced: number; skipped: number }>(`/projects/${projectId}/gitlab/sync`, undefined, { params: since ? { since } : undefined }),
  syncGitlabDocs: (projectId: string) => api.post<{ readme: { pulled: boolean; pushed: boolean; sha?: string; noFile?: boolean } | null; techSpec: { pulled: boolean; pushed: boolean; sha?: string; noFile?: boolean } | null }>(`/projects/${projectId}/gitlab/sync-docs`),
  connectGitlab: (projectId: string, repoUrl: string) => api.post<{ success: boolean; message: string; gitlabProjectId?: number }>(`/projects/${projectId}/gitlab/connect`, { repoUrl }),
  disconnectGitlab: (projectId: string) => api.delete<{ success: boolean; message: string }>(`/projects/${projectId}/gitlab`),
  setupGitlabWebhook: (projectId: string) => api.post<{ success: boolean; message: string }>(`/projects/${projectId}/gitlab/webhook`),

  // Knowledge Base sync
  syncDocsToKb: (projectId: string) => api.post<{
    success: boolean
    message: string
    readme: { id: string; title: string } | null
    techSpec: { id: string; title: string } | null
  }>(`/projects/${projectId}/sync-docs-to-kb`),
  getKbArticles: (projectId: string) => api.get<{
    articles: Array<{
      id: string
      title: string
      slug: string
      sourceDocType: string
      status: string
      folderId: string | null
      publishedAt: string | null
      updatedAt: string
    }>
    total: number
  }>(`/projects/${projectId}/kb-articles`),

  // Documentation Tree (GitLab full doc sync)
  getDocsTree: (projectId: string) => api.get<{
    total: number
    markdown: number
    images: number
    pdfs: number
    other: number
    files: Array<{
      id: string
      name: string
      type: 'tree' | 'blob'
      path: string
      mode: string
    }>
  }>(`/projects/${projectId}/docs/tree`),

  getTrackedDocs: (projectId: string) => api.get<Array<{
    id: string
    projectId: string
    filePath: string
    fileType: 'markdown' | 'image' | 'pdf' | 'other'
    articleId: string | null
    storageKey: string | null
    lastSyncSha: string | null
    lastSyncedAt: string | null
    syncStatus: 'synced' | 'pending_pull' | 'pending_push' | 'conflict' | 'error'
    linkedFiles: string[]
    errorMessage: string | null
    article: {
      id: string
      title: string
      slug: string
      status: string
    } | null
  }>>(`/projects/${projectId}/docs`),

  getDocsStatus: (projectId: string) => api.get<{
    total: number
    synced: number
    pendingPull: number
    pendingPush: number
    conflict: number
    error: number
  }>(`/projects/${projectId}/docs/status`),

  importAllDocs: (projectId: string) => api.post<{
    success: boolean
    imported: number
    updated: number
    failed: number
    errors: string[]
  }>(`/projects/${projectId}/docs/import`),

  syncDocFile: (projectId: string, filePath: string) =>
    api.post<{
      success: boolean
      created: boolean
      updated: boolean
      docFile: unknown
    }>(`/projects/${projectId}/docs/sync/${filePath}`),

  pushDocFile: (projectId: string, filePath: string, commitMessage: string) =>
    api.post<{
      success: boolean
      commitSha?: string
      message?: string
    }>(`/projects/${projectId}/docs/push/${filePath}`, { commitMessage }),

  // GitLab Commits
  getCommits: (projectId: string, params?: {
    status?: CommitClassificationStatus
    technical?: boolean
    limit?: number
    offset?: number
  }) => api.get<{ commits: GitLabCommit[]; total: number }>(`/projects/${projectId}/gitlab/commits`, { params }),
  getCommitStats: (projectId: string) => api.get<CommitStats>(`/projects/${projectId}/gitlab/commits/stats`),
  getCommit: (projectId: string, commitId: string) => api.get<GitLabCommit>(`/projects/${projectId}/gitlab/commits/${commitId}`),
  linkTasksToCommit: (projectId: string, commitId: string, taskIds: string[]) =>
    api.post<GitLabCommit>(`/projects/${projectId}/gitlab/commits/${commitId}/link`, { taskIds }),
  reclassifyCommit: (projectId: string, commitId: string) =>
    api.post<{ success: boolean; message: string }>(`/projects/${projectId}/gitlab/commits/${commitId}/reclassify`),
  classifyPendingCommits: (projectId: string) =>
    api.post<{ success: boolean; queued: number; message: string }>(`/projects/${projectId}/gitlab/commits/classify-pending`),

  // Semantic commit search across all projects (RAG)
  searchCommits: (query: string, options?: { projectId?: string; limit?: number }) =>
    api.get<{
      commits: Array<{
        commitId: string
        projectId: string
        shortSha: string
        title: string
        authorName: string
        authoredDate: string
        similarity: number
        project?: { id: string; title: string; slug: string }
      }>
      total: number
    }>('/projects/commits/search', {
      params: { q: query, ...options },
    }),

  // Status Transition Rules
  // Проекты, к которым можно привязывать задачи по подразделению (включая невидимые предков)
  getLinkable: () =>
    api.get<Array<{ id: string; title: string; slug: string }>>('/projects/linkable'),

  // Кто может привязывать задачи к проекту: подразделение (по умолчанию) или только участники
  getTaskLinkScope: (projectId: string) =>
    api.get<{ scope: 'department' | 'members' }>(`/projects/${projectId}/settings/task-link-scope`),

  updateTaskLinkScope: (projectId: string, scope: 'department' | 'members') =>
    api.patch<{ scope: 'department' | 'members' }>(`/projects/${projectId}/settings/task-link-scope`, { scope }),

  getTransitionRules: (projectId: string) =>
    api.get<{
      rules: Array<{
        id: string
        fromStatus: string[] | '*'
        toStatus: string
        requireComment: boolean
        notifications: {
          email: boolean
          telegram: boolean
          recipients: 'owner' | 'assignees' | 'all'
        }
        enabled: boolean
      }>
    }>(`/projects/${projectId}/settings/transition-rules`),

  updateTransitionRules: (
    projectId: string,
    rules: Array<{
      id: string
      fromStatus: string[] | '*'
      toStatus: string
      requireComment: boolean
      notifications: {
        email: boolean
        telegram: boolean
        recipients: 'owner' | 'assignees' | 'all'
      }
      enabled: boolean
    }>
  ) => api.patch(`/projects/${projectId}/settings/transition-rules`, { rules }),
}

export const knowledgeApi = {
  // Articles CRUD
  getAll: (params?: ArticleFilters) => api.get('/knowledge', { params }),
  getOne: (id: string) => api.get(`/knowledge/${id}`),
  getBySlug: (slug: string) => api.get(`/knowledge/slug/${slug}`),
  create: (data: CreateArticleInput) => api.post('/knowledge', data),
  update: (id: string, data: UpdateArticleInput) => api.put(`/knowledge/${id}`, data),
  delete: (id: string) => api.delete(`/knowledge/${id}`),
  publish: (id: string) => api.post(`/knowledge/${id}/publish`),
  archive: (id: string) => api.post(`/knowledge/${id}/archive`),
  revertToPublished: (id: string) => api.post(`/knowledge/${id}/revert`),

  // File upload
  upload: (file: File, folderId?: string) => {
    const formData = new FormData()
    formData.append('file', file)
    if (folderId) {
      formData.append('folderId', folderId)
    }
    return api.post<{ article: KnowledgeArticle; warnings?: string[] }>('/knowledge/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  // Folders
  getFolders: () => api.get<KnowledgeFolderTree[]>('/knowledge/folders'),
  getFolder: (id: string) => api.get<KnowledgeFolder>(`/knowledge/folders/${id}`),
  createFolder: (data: CreateFolderInput) => api.post<KnowledgeFolder>('/knowledge/folders', data),
  updateFolder: (id: string, data: UpdateFolderInput) => api.put<KnowledgeFolder>(`/knowledge/folders/${id}`, data),
  deleteFolder: (id: string) => api.delete(`/knowledge/folders/${id}`),

  // Meta
  getCategories: () => api.get<string[]>('/knowledge/meta/categories'),
  getTags: () => api.get<string[]>('/knowledge/meta/tags'),
  getStats: () => api.get<Record<ArticleStatus, number>>('/knowledge/meta/stats'),

  // Embeddings
  generateEmbeddings: () =>
    api.post<{
      total: number
      success: number
      failed: number
      errors: Array<{ articleId: string; error: string }>
    }>('/knowledge/embeddings/generate'),
}

// Strategy Types (unified strategy module)
export interface StrategyGoal {
  id: string
  humanId?: string
  code: number
  title: string
  description: string | null
  progress: number
  federalTarget: number | null
  targetYear: number | null
  sortOrder: number
  metadata: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

export interface RadarDirectionPoint {
  id: string
  code: number
  name: string
  baseline: number
  current: number
  expectedNow: number
  target2026: number
  target2030: number
}

export interface StrategyDirection {
  id: string
  humanId?: string
  code: number
  name: string
  description: string | null
  baselineScore: number
  currentScore: number
  targetScore2026: number
  targetScore: number
  progress: number
  responsibleDept: string | null
  sortOrder: number
  metadata: Record<string, unknown>
  measures?: StrategyMeasure[]
  goalLinks?: DirectionGoalLink[]
  createdAt: string
  updatedAt: string
}

export interface DirectionGoalLink {
  id: string
  directionId: string
  goalId: string
  weight: number
  createdAt: string
}

export interface StrategyMeasure {
  id: string
  humanId?: string
  directionId: string
  direction?: StrategyDirection
  code: string
  name: string
  description: string | null
  deadline: string | null
  responsible: string | null
  responsiblePersonId: string | null
  responsiblePerson?: { id: string; fullName: string }
  status: MeasureStatus
  progress: number
  manualProgress: number | null
  progressMode: string
  expectedEffort: number
  completionCriteria: string | null
  sortOrder: number
  metadata: Record<string, unknown>
  tasks?: Array<{ id: string; humanId: string; title: string; status: string; progress: number; impactWeight: number }>
  totalTaskCount?: number
  createdAt: string
  updatedAt: string
}

export interface StrategicDashboard {
  goals: Array<{
    id: string; code: number; title: string
    progress: number; federalTarget: number | null
  }>
  overallProgress: number
  radarData: RadarDirectionPoint[]
  directionsSummary: Array<{
    id: string; code: number; name: string
    progress: number; status: 'green' | 'yellow' | 'red'
  }>
  criticalDeviations: Array<{
    measureId: string; name: string; deadline: string
    progress: number; directionName: string
  }>
  aiSummary: string | null
}

export interface ManagementDashboard {
  directions: Array<{
    id: string; code: number; name: string
    baselineScore: number; currentScore: number; targetScore2026: number
    targetScore: number; gap: number
    progress: number; measuresCount: number; measuresCompleted: number
  }>
  radarData: RadarDirectionPoint[]
  gapAnalysis: Array<{
    directionId: string; directionName: string; baseline: number; current: number
    expectedNow: number; target2026: number
    target: number; gap: number; progress: number
    trend: 'up' | 'down' | 'flat'
  }>
  deviations: Array<{
    measureId: string; name: string; deadline: string
    progress: number; directionName: string; responsible: string
  }>
  timeline: Array<{
    measureId: string; code: string; name: string
    deadline: string | null; progress: number; status: string
    directionCode: number
  }>
}

export interface OperationalDashboard {
  stats: {
    totalTasks: number; linkedTasks: number; unlinkedTasks: number
    avgMeasureProgress: number; blockedTasks: number
  }
  measuresByDirection: Array<{
    directionId: string; directionName: string
    measures: Array<{ id: string; humanId?: string; name: string; progress: number; taskCount: number }>
  }>
}

export interface FunctionalDashboard {
  myMeasures: Array<{
    id: string; name: string; progress: number
    deadline: string | null; directionName: string
  }>
  myTasks: Array<{
    id: string; title: string; status: string
    measureName: string; directionName: string
  }>
  myContribution: Array<{
    directionName: string; tasksCompleted: number; tasksTotal: number
  }>
}

export interface StrategyReport {
  id: string
  type: string
  content: string
  model: string
  createdAt: string
}

// WeeklyDigest and ExecutiveKpi removed (executive module deprecated, replaced by strategy module)

// executiveApi removed (replaced by strategyApi)

export const strategyApi = {
  // Goals
  getGoals: () => api.get<StrategyGoal[]>('/strategy/goals'),
  getGoal: (id: string) => api.get<StrategyGoal>(`/strategy/goals/${id}`),
  updateGoal: (id: string, data: Partial<{ title: string; description: string; targetYear: number; federalTarget: number; sortOrder: number }>) =>
    api.put<StrategyGoal>(`/strategy/goals/${id}`, data),

  // Directions
  getDirections: () => api.get<StrategyDirection[]>('/strategy/directions'),
  getDirection: (id: string) => api.get<StrategyDirection>(`/strategy/directions/${id}`),
  updateDirection: (id: string, data: Partial<{ name: string; description: string; currentScore: number; targetScore: number; responsibleDept: string }>) =>
    api.put<StrategyDirection>(`/strategy/directions/${id}`, data),

  // Direction-Goal Links
  createLink: (data: { directionId: string; goalId: string; weight: number }) =>
    api.post<DirectionGoalLink>('/strategy/direction-goal-links', data),
  deleteLink: (id: string) => api.delete(`/strategy/direction-goal-links/${id}`),

  // Measures
  getMeasures: (params?: { directionId?: string }) =>
    api.get<StrategyMeasure[]>('/strategy/measures', { params }),
  getMeasure: (id: string) => api.get<StrategyMeasure>(`/strategy/measures/${id}`),
  createMeasure: (data: { directionId: string; code: string; name: string; description?: string; deadline?: string; responsible?: string; expectedEffort?: number; progressMode?: string }) =>
    api.post<StrategyMeasure>('/strategy/measures', data),
  updateMeasure: (id: string, data: Partial<{ name: string; description: string; deadline: string; responsible: string; status: MeasureStatus; expectedEffort: number; progressMode: string }>) =>
    api.put<StrategyMeasure>(`/strategy/measures/${id}`, data),
  deleteMeasure: (id: string) => api.delete(`/strategy/measures/${id}`),
  linkTasks: (measureId: string, taskIds: string[], weight?: number) =>
    api.post(`/strategy/measures/${measureId}/link-tasks`, { taskIds, ...(weight != null && { weight }) }),
  unlinkTask: (measureId: string, taskId: string) =>
    api.post(`/strategy/measures/${measureId}/unlink-task/${taskId}`),
  updateProgress: (measureId: string, progress: number) =>
    api.put(`/strategy/measures/${measureId}/progress`, { progress }),

  // Dashboards
  getStrategicDashboard: () => api.get<StrategicDashboard>('/strategy/dashboard/strategic'),
  getManagementDashboard: () => api.get<ManagementDashboard>('/strategy/dashboard/management'),
  getOperationalDashboard: () => api.get<OperationalDashboard>('/strategy/dashboard/operational'),
  getFunctionalDashboard: () => api.get<FunctionalDashboard>('/strategy/dashboard/functional'),

  // AI
  getAISummary: () => api.get<string | null>('/strategy/ai/summary'),
  getAIReports: (params?: { type?: string; limit?: number }) =>
    api.get<StrategyReport[]>('/strategy/ai/reports', { params }),

  // Admin
  recalculate: () => api.post<void>('/strategy/admin/recalculate'),
  getOverallProgress: () => api.get<number>('/strategy/admin/overall-progress'),
  seed: () => api.post('/strategy/admin/seed'),

  // Classification
  classifyTask: (taskId: string) =>
    api.post(`/strategy/classify/${taskId}`),
  getClassifications: (params?: Record<string, any>) =>
    api.get("/strategy/classifications", { params }),
  getClassificationStats: () =>
    api.get("/strategy/classifications/stats"),
  reviewClassification: (id: string, data: { status: string; weight?: number }) =>
    api.patch(`/strategy/classifications/${id}`, data),
  batchReviewClassifications: (data: { ids: string[]; status: string }) =>
    api.post("/strategy/classifications/batch", data),
}

// System Settings (superadmin only)
export interface SystemSettingItem {
  key: string
  value: string | null
  description: string | null
  isSecret: boolean
  updatedBy: string | null
  createdAt: string
  updatedAt: string
}

export interface GitLabConfig {
  baseUrl: string | null
  webhookUrl: string | null
  hasToken: boolean
}

export interface TelegramListenerConfig {
  enabled: boolean
  hasToken: boolean
  webhookSecret: string | null
}

export const settingsApi = {
  // Get all settings (superadmin only)
  getAll: () => api.get<SystemSettingItem[]>('/settings'),

  // Get specific setting
  get: (key: string) => api.get<SystemSettingItem>(`/settings/${key}`),

  // Set specific setting
  set: (key: string, value: string, description?: string, isSecret?: boolean) =>
    api.put<SystemSettingItem>(`/settings/${key}`, { value, description, isSecret }),

  // GitLab configuration
  getGitLabConfig: () => api.get<GitLabConfig>('/settings/gitlab'),
  setGitLabConfig: (baseUrl: string, apiToken: string, webhookUrl?: string) =>
    api.put<{ success: boolean; message: string }>('/settings/gitlab', { baseUrl, apiToken, webhookUrl }),
  removeGitLabConfig: () => api.delete('/settings/gitlab'),

  // Telegram Listener configuration
  getTelegramConfig: () => api.get<TelegramListenerConfig>('/settings/telegram'),
  setTelegramConfig: (config: { enabled?: boolean; botToken?: string; webhookSecret?: string }) =>
    api.put<{ success: boolean; message: string }>('/settings/telegram', config),
  removeTelegramConfig: () => api.delete('/settings/telegram'),
}

// Tags API
export interface TagItem {
  id: string
  name: string
  color: string
  description?: string
  usageCount: number
  isAiGenerated: boolean
  /** Группа тегов (не более одной) */
  groupId?: string | null
  /** Свод для отчёта: Новое / Развитие / Поддержка */
  rollup?: string | null
  /** Область тега: null — общий, иначе подразделение */
  departmentId?: string | null
  departmentName?: string | null
  createdAt: string
  updatedAt: string
}

// Группы тегов
export interface TagGroupItem {
  id: string
  name: string
  description: string | null
  sortOrder: number
  isRequired: boolean
  autoClassify: boolean
  confidenceThreshold: number
  tags: TagItem[]
}

export type TagGroupInput = Partial<
  Pick<TagGroupItem, 'name' | 'description' | 'sortOrder' | 'isRequired' | 'autoClassify' | 'confidenceThreshold'>
>

export type TagTimeDimension = 'person' | 'project' | 'month'

export interface TagTimeReportRow {
  key: string
  label: string
  byTag: Record<string, number>
  unlabeled: number
  total: number
}

export interface TagTimeReport {
  groupName: string
  from: string
  to: string
  dimension: TagTimeDimension
  tags: Array<{ name: string; rollup: string | null }>
  rows: TagTimeReportRow[]
  totals: { byTag: Record<string, number>; unlabeled: number; total: number }
  rollup: Array<{ name: string; minutes: number }>
}

export interface TagClassificationRecord {
  groupId: string
  groupName: string
  source: 'llm' | 'rule'
  items: Array<{ tag: string; confidence: number; reason: string }>
  applied: string[]
  skipped: 'manual' | 'none' | null
  threshold: number
}

export interface UnlabeledTaskItem {
  id: string
  humanId: string | null
  title: string
  status: string
  tags: string[]
  classification: TagClassificationRecord | null
}

export interface ReclassifyTagsResult {
  group: string
  dryRun: boolean
  selected: number
  byRule: number
  queued: number
  failed: number
}

export const tagGroupsApi = {
  getAll: () => api.get<TagGroupItem[]>('/tag-groups'),
  create: (data: TagGroupInput) => api.post<TagGroupItem>('/tag-groups', data),
  update: (id: string, data: TagGroupInput) => api.patch<TagGroupItem>(`/tag-groups/${id}`, data),
  delete: (id: string) => api.delete(`/tag-groups/${id}`),
  timeReport: (
    id: string,
    params: { from: string; to: string; groupBy: TagTimeDimension; projectId?: string },
  ) => api.get<TagTimeReport>(`/tag-groups/${id}/time-report`, { params }),
  unlabeled: (id: string, params: { since?: string; limit?: number; offset?: number }) =>
    api.get<{ total: number; items: UnlabeledTaskItem[] }>(`/tag-groups/${id}/unlabeled`, { params }),
  // Ретроразметка (admin): по умолчанию dryRun — только подсчёт
  reclassify: (data: { since: string; until?: string; onlyUnlabeled?: boolean; dryRun?: boolean; limit?: number }) =>
    api.post<ReclassifyTagsResult>('/ai/annotations/reclassify-tags', data),
}

// ───────────── Метрики потока задач ─────────────

export interface FlowDurationStats {
  count: number
  median: number | null
  p85: number | null
  mean: number | null
}

export interface FlowAgingItem {
  id: string
  humanId: string | null
  title: string
  status: string
  ageDays: number
  inStatusDays: number
}

export interface FlowReport {
  from: string
  to: string
  summary: {
    completed: number
    lead: FlowDurationStats
    cycle: FlowDurationStats & { coverage: number | null }
  }
  timeInStatus: Array<{ status: string; stats: FlowDurationStats; share: number }>
  throughput: Array<{ week: string; completed: number }>
  cfd: { days: string[]; series: Record<string, number[]> }
  aging: {
    buckets: Array<{ status: string; counts: number[] }>
    bucketLabels: string[]
    oldest: FlowAgingItem[]
  }
  quality: { tasks: number; reconstructed: number; mismatches: number }
}

export const flowMetricsApi = {
  report: (params: {
    from: string
    to: string
    projectId?: string
    departmentId?: string
    includeAuto?: boolean
  }) => api.get<FlowReport>('/reports/flow', { params }),
}

export const tagsApi = {
  // Get all tags
  getAll: () => api.get<TagItem[]>('/tags'),

  // Get tag by ID
  getOne: (id: string) => api.get<TagItem>(`/tags/${id}`),

  // Create tag
  create: (data: { name: string; color?: string; description?: string; departmentId?: string | null }) =>
    api.post<TagItem>('/tags', data),

  // Update tag
  update: (
    id: string,
    data: {
      name?: string
      color?: string
      description?: string
      groupId?: string | null
      rollup?: string | null
      departmentId?: string | null
    },
  ) => api.patch<TagItem>(`/tags/${id}`, data),

  // Delete tag
  delete: (id: string) => api.delete(`/tags/${id}`),

  // Sync tags from tasks
  sync: () => api.post<{ created: number; updated: number }>('/tags/sync'),

  // Get metadata for multiple tags
  getMetadata: (tags: string[]) =>
    api.get<Record<string, { color: string; description?: string }>>('/tags/metadata/bulk', { params: { tags } }),
}

// ==================== Digital Maturity API ====================

export type MaturityLevel = 'initial' | 'developing' | 'defined' | 'managed' | 'optimizing'
export type MaturityDimension = 'infrastructure' | 'management' | 'education' | 'security' | 'federal_systems' | 'digital_literacy' | 'analytics'

export interface DimensionScore {
  id: string
  assessmentId: string
  dimension: MaturityDimension
  score: number
  level: MaturityLevel
  weight: number
  strengths: string[]
  weaknesses: string[]
  recommendations: string[]
  indicators: Record<string, unknown>
  comment?: string
  sources: string[]
}

export interface DigitalMaturityAssessment {
  id: string
  assessmentDate: string
  assessmentYear: number
  overallScore: number
  overallLevel: MaturityLevel
  title?: string
  description?: string
  strengths: string[]
  weaknesses: string[]
  recommendations: string[]
  aiAnalysis?: string
  aiModelUsed?: string
  isPublished: boolean
  assessedById?: string
  assessedBy?: { id: string; fullName: string }
  dimensionScores: DimensionScore[]
  metadata: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

export interface DigitalMaturityTarget {
  id: string
  targetYear: number
  dimension: MaturityDimension
  targetScore: number
  baselineScore?: number
  initiatives: string[]
  responsibleId?: string
  responsible?: { id: string; fullName: string }
  comment?: string
  status: string
  achievedAt?: string
  createdById?: string
  createdBy?: { id: string; fullName: string }
  createdAt: string
  updatedAt: string
}

export interface DigitalMaturitySummary {
  current: DigitalMaturityAssessment | null
  targets: DigitalMaturityTarget[]
  dimensionLabels: Record<MaturityDimension, string>
  levelLabels: Record<MaturityLevel, string>
}

export interface CreateAssessmentDto {
  assessmentDate: string
  title?: string
  description?: string
  dimensionScores: {
    dimension: MaturityDimension
    score: number
    weight?: number
    strengths?: string[]
    weaknesses?: string[]
    recommendations?: string[]
    indicators?: Record<string, unknown>
    comment?: string
    sources?: string[]
  }[]
  strengths?: string[]
  weaknesses?: string[]
  recommendations?: string[]
  isPublished?: boolean
  metadata?: Record<string, unknown>
}

export interface CreateTargetDto {
  targetYear: number
  dimension: MaturityDimension
  targetScore: number
  baselineScore?: number
  initiatives?: string[]
  responsibleId?: string
  comment?: string
}

// ==================== Digitalization Types ====================

export type MeasureStatus = 'planned' | 'in_progress' | 'completed' | 'delayed' | 'cancelled'

export interface DigitalizationDirection {
  id: string
  code: number
  name: string
  dimension: MaturityDimension
  description: string | null
  currentScore: number
  targetScore: number
  targetYear: number
  weight: number
  responsibleDept: string | null
  progress: number
  sortOrder: number
  metadata: Record<string, unknown>
  measures?: DigitalizationMeasure[]
  createdAt: string
  updatedAt: string
}

export interface DigitalizationMeasure {
  id: string
  directionId: string
  direction?: DigitalizationDirection
  code: string
  name: string
  description: string | null
  deadline: string | null
  responsible: string | null
  responsiblePersonId: string | null
  responsiblePerson?: { id: string; fullName: string }
  status: MeasureStatus
  progress: number
  sortOrder: number
  metadata: Record<string, unknown>
  tasks?: Array<{ id: string; humanId: string; title: string; status: string; progress: number }>
  createdAt: string
  updatedAt: string
}

export interface DigitalizationDashboard {
  overallProgress: number
  currentMaturityScore: number
  targetMaturityScore: number
  gap: number
  totalDirections: number
  totalMeasures: number
  completedMeasures: number
  inProgressMeasures: number
  totalLinkedTasks: number
  completedTasks: number
  directions: Array<{
    id: string
    code: number
    name: string
    dimension: MaturityDimension
    progress: number
    currentScore: number
    targetScore: number
    measuresCount: number
    completedMeasures: number
  }>
}

export interface GapAnalysisItem {
  direction: string
  dimension: MaturityDimension
  currentScore: number
  targetScore: number
  gap: number
  progress: number
  status: 'achieved' | 'on_track' | 'attention' | 'critical'
}

export interface TimelineMeasure {
  id: string
  code: string
  name: string
  directionName: string
  deadline: string | null
  status: MeasureStatus
  progress: number
  responsible: string | null
}

// ==================== Time Tracking API ====================

export type TimeEntryType = 'task' | 'break' | 'planning' | 'meeting' | 'other'

export interface TimeEntry {
  id: string
  personId: string
  person?: { id: string; fullName: string }
  taskId: string | null
  task?: { id: string; humanId: string; title: string; status?: string } | null
  startedAt: string
  endedAt: string | null
  durationMinutes: number | null
  isBreak: boolean
  entryType: TimeEntryType
  description: string | null
  summary: string | null
  commitSha: string | null
  source: string
  createdAt: string
}

/** Result of AI analysis for a time tracking comment */
export interface CommentAnalysisResult {
  /** false if the comment is garbage (random chars, just numbers, etc.) */
  isValid: boolean
  /** true if the comment has substantive content */
  isMeaningful: boolean
  /** true if the comment relates to the task context */
  isRelevantToTask: boolean
  /** Overall quality assessment */
  quality: 'poor' | 'acceptable' | 'good'
  /** AI-generated summary for reports (1-2 sentences) */
  summary: string | null
  /** Improvement suggestions */
  suggestions: string[]
  /** Confidence score 0-1 */
  confidence: number
}

export interface TimeTrackingStatus {
  activeEntry: TimeEntry | null
  assignedTasks: Array<{
    id: string
    humanId: string
    title: string
    status: string
    priority: string
  }>
}

export interface DailySummary {
  totalMinutes: number
  workMinutes: number
  breakMinutes: number
  entries: TimeEntry[]
}

export interface TaskTimeReport {
  entries: TimeEntry[]
  totalMinutes: number
  /** Зонт: время потомков по связям hierarchy; treeMinutes — своё + потомков */
  descendants?: { descendantCount: number; minutes: number; treeMinutes: number }
}

export const timeTrackingApi = {
  // Get current status (active entry + assigned tasks)
  getStatus: () => api.get<TimeTrackingStatus>('/time-tracking/status'),

  // Start tracking a task
  startTask: (taskId: string, description?: string) =>
    api.post<TimeEntry>('/time-tracking/start-task', { taskId, description }),

  // Start a break
  startBreak: (description?: string) =>
    api.post<TimeEntry>('/time-tracking/start-break', { description }),

  // Start an activity (planning, meeting, other)
  startActivity: (entryType: 'planning' | 'meeting' | 'other', description?: string) =>
    api.post<TimeEntry>('/time-tracking/start-activity', { entryType, description }),

  // Stop break and resume interrupted task
  stopBreak: () =>
    api.post<{ stoppedBreak: TimeEntry; resumedEntry: TimeEntry | null }>('/time-tracking/stop-break'),

  // Check recent activity for current task (comments in last 30 min)
  checkActivity: () =>
    api.get<{
      hasActiveTask: boolean
      taskId?: string
      taskTitle?: string
      hasRecentComments: boolean
      commentCount: number
    }>('/time-tracking/check-activity'),

  // Analyze comment using AI (returns quality, summary, suggestions)
  analyzeComment: (description: string, taskId?: string) =>
    api.post<CommentAnalysisResult>('/time-tracking/analyze-comment', { description, taskId }),

  // Stop current tracking (optional description and AI summary saved to time entry)
  stop: (description?: string, summary?: string) =>
    api.post<TimeEntry | null>('/time-tracking/stop', { description, summary }),

  // Switch to another task (stops current with description/summary, starts new)
  switchTask: (taskId: string, description?: string, summary?: string) =>
    api.post<TimeEntry>(`/time-tracking/switch/${taskId}`, { description, summary }),

  // Get my entries
  getMyEntries: (startDate?: string, endDate?: string) =>
    api.get<TimeEntry[]>('/time-tracking/my-entries', { params: { startDate, endDate } }),

  // Get daily summary
  getDailySummary: (date?: string) =>
    api.get<DailySummary>('/time-tracking/daily-summary', { params: { date } }),

  // Get time entries for a task
  getTaskEntries: (taskId: string) =>
    api.get<TaskTimeReport>(`/time-tracking/task/${taskId}`),

  // Фактическое время по задачам с разбивкой по дням МСК (Гант)
  getTasksDailyTime: (taskIds: string[]) =>
    api.post<{
      tasks: Record<string, { totalMinutes: number; days: Array<{ day: string; minutes: number }> }>
    }>('/time-tracking/tasks-daily', { taskIds }),

  // Link commit to current entry
  linkCommit: (commitSha: string) =>
    api.post<TimeEntry | null>('/time-tracking/link-commit', { commitSha }),

  // Create manual entry
  createEntry: (data: {
    taskId?: string | null
    startedAt: string
    endedAt?: string
    durationMinutes?: number
    description?: string
    isBreak?: boolean
    entryType?: TimeEntryType
    commitSha?: string
  }) => api.post<TimeEntry>('/time-tracking/entries', data),

  // Get single entry
  getEntry: (entryId: string) =>
    api.get<TimeEntry>(`/time-tracking/entries/${entryId}`),

  // Update entry
  updateEntry: (entryId: string, data: {
    taskId?: string | null
    startedAt?: string
    endedAt?: string
    durationMinutes?: number
    description?: string
    isBreak?: boolean
    entryType?: TimeEntryType
    commitSha?: string
  }) => api.patch<TimeEntry>(`/time-tracking/entries/${entryId}`, data),

  // Delete entry
  deleteEntry: (entryId: string) =>
    api.delete(`/time-tracking/entries/${entryId}`),

  // ==================== ADMIN ENDPOINTS ====================

  // Get overview of all employees
  adminGetOverview: (startDate?: string, endDate?: string) =>
    api.get<EmployeeOverview[]>('/time-tracking/admin/overview', { params: { startDate, endDate } }),

  // Get daily summary for all employees
  adminGetDaily: (date?: string) =>
    api.get<EmployeeDailySummary[]>('/time-tracking/admin/daily', { params: { date } }),

  // Get workload by tasks
  adminGetWorkload: (startDate?: string, endDate?: string) =>
    api.get<EmployeeWorkloadItem[]>('/time-tracking/admin/workload', { params: { startDate, endDate } }),

  // Get detailed entries for a specific employee
  adminGetEmployeeEntries: (personId: string, startDate?: string, endDate?: string) =>
    api.get<EmployeeDayEntries[]>(`/time-tracking/admin/employee/${personId}`, { params: { startDate, endDate } }),

  // Check if current user has admin access (superadmin or has subordinates)
  adminCheckAccess: () =>
    api.get<{ hasAccess: boolean; isSuperAdmin: boolean; subordinateCount: number }>('/time-tracking/admin/access'),
}

// Admin time-tracking types
export interface EmployeeOverview {
  person: {
    id: string
    fullName: string
    shortName: string
    position: string | null
  }
  totalWorkMinutes: number
  totalBreakMinutes: number
  taskCount: number
  activeEntry: TimeEntry | null
  lastActivityAt: string | null // Most recent activity timestamp for sorting
}

export interface EmployeeDailySummary {
  person: {
    id: string
    fullName: string
    shortName: string
    position: string | null
  }
  date: string
  workMinutes: number
  breakMinutes: number
  totalMinutes: number
  taskCount: number
  entries: TimeEntry[]
}

export interface EmployeeWorkloadItem {
  person: {
    id: string
    fullName: string
    shortName: string
  }
  task: {
    id: string
    humanId: string
    title: string
    status: string
  }
  totalMinutes: number
  entryCount: number
}

export interface EmployeeDayEntries {
  date: string
  entries: TimeEntry[]
  workMinutes: number
  breakMinutes: number
}

// ==================== TIMESHEET API ====================

export type AttendanceCode = 'Я' | 'УД' | 'В' | 'О' | 'Б' | 'К' | 'РВ' | 'ПР' | ''
export type ProgressStatus = 'ahead' | 'on_track' | 'behind'
export type TimesheetPeriod = 'month' | 'week'
export type DayType = 'working' | 'weekend' | 'holiday' | 'pre_holiday'

export interface WorkCalendarDay {
  id: string
  date: string
  dayType: DayType
  hours: number
  description?: string
}

export interface WorkCalendarStats {
  workingDays: number
  totalHours: number
  holidays: number
  weekends: number
}

export interface TimesheetProgress {
  planned: number
  actual: number
  percent: number
  status: ProgressStatus
  plannedTotal: number
}

export interface PersonBrief {
  id: string
  fullName: string
  shortName?: string
  position?: string
  employmentRate: number
}

export interface EmployeeTimesheetSummary {
  person: PersonBrief
  planned: number
  actual: number
  percent: number
  status: ProgressStatus
}

export interface TimeEntryBrief {
  id: string
  entryType: string
  startedAt: string
  endedAt?: string
  durationMinutes?: number
  taskId?: string
  taskTitle?: string
  description?: string
}

export interface AbsenceBrief {
  type: string
  comment?: string
}

export interface DayRecord {
  date: string
  dayType: DayType
  plannedHours: number
  actualHours: number
  code: AttendanceCode
  entries: TimeEntryBrief[]
  absence?: AbsenceBrief
}

export interface HalfMonthTotals {
  first: number
  second: number
}

export interface TimesheetSummary {
  plannedHours: number
  actualHours: number
  plannedToDate: number
  percent: number
  status: ProgressStatus
}

export interface EmployeeTimesheet {
  person: PersonBrief
  period: {
    start: string
    end: string
    type: TimesheetPeriod
  }
  summary: TimesheetSummary
  days: DayRecord[]
  halfMonthTotals?: HalfMonthTotals
}

export const timesheetApi = {
  // === Calendar ===
  getCalendar: (year?: number) =>
    api.get<{ year: number; calendar: WorkCalendarDay[]; stats: WorkCalendarStats }>(
      '/timesheet/calendar',
      { params: { year } }
    ),

  importCalendar: (year: number) =>
    api.post<{ success: boolean; year: number; imported: number }>(
      '/timesheet/calendar/import',
      { year }
    ),

  updateCalendarDay: (date: string, data: Partial<Pick<WorkCalendarDay, 'dayType' | 'hours' | 'description'>>) =>
    api.put<WorkCalendarDay>(`/timesheet/calendar/${date}`, data),

  // === My Timesheet ===
  getMyTimesheet: (params?: { period?: TimesheetPeriod; date?: string }) =>
    api.get<EmployeeTimesheet>('/timesheet/my', { params }),

  getMyProgress: () =>
    api.get<TimesheetProgress>('/timesheet/my/progress'),

  // === Employees ===
  getEmployeesTimesheet: (params?: { period?: TimesheetPeriod; date?: string; showAll?: boolean }) =>
    api.get<{ employees: EmployeeTimesheetSummary[] }>('/timesheet/employees', { params }),

  getEmployeeDetail: (personId: string, params?: { period?: TimesheetPeriod; date?: string }) =>
    api.get<EmployeeTimesheet>(`/timesheet/employees/${personId}`, { params }),

  // === Export ===
  exportT13: (month?: string, personIds?: string[]) =>
    api.get('/timesheet/export', {
      params: { month, personIds: personIds?.join(',') },
      responseType: 'blob',
    }),

  // === Access Log (СКУД - stub) ===
  getAccessLog: (personId: string, params?: { startDate?: string; endDate?: string }) =>
    api.get<{ personId: string; period: { start?: string; end?: string }; events: unknown[]; message: string }>(
      `/timesheet/access-log/${personId}`,
      { params }
    ),
}

export const digitalMaturityApi = {
  // Assessments
  getCurrent: () => api.get<DigitalMaturityAssessment | null>('/digital-maturity/current'),
  getSummary: () => api.get<DigitalMaturitySummary>('/digital-maturity/summary'),
  getHistory: (params?: { limit?: number; offset?: number; publishedOnly?: boolean }) =>
    api.get<{ data: DigitalMaturityAssessment[]; total: number }>('/digital-maturity/history', { params }),
  getAssessment: (id: string) => api.get<DigitalMaturityAssessment>(`/digital-maturity/assessments/${id}`),
  createAssessment: (data: CreateAssessmentDto) =>
    api.post<DigitalMaturityAssessment>('/digital-maturity/assessments', data),
  updateAssessment: (id: string, data: Partial<CreateAssessmentDto>) =>
    api.put<DigitalMaturityAssessment>(`/digital-maturity/assessments/${id}`, data),
  publishAssessment: (id: string) =>
    api.post<DigitalMaturityAssessment>(`/digital-maturity/assessments/${id}/publish`),
  deleteAssessment: (id: string) => api.delete(`/digital-maturity/assessments/${id}`),

  // Targets
  getAllTargets: () => api.get<{ byYear: Record<number, DigitalMaturityTarget[]> }>('/digital-maturity/targets'),
  getTargetsByYear: (year: number) => api.get<DigitalMaturityTarget[]>(`/digital-maturity/targets/${year}`),
  createTarget: (data: CreateTargetDto) => api.post<DigitalMaturityTarget>('/digital-maturity/targets', data),
  bulkCreateTargets: (year: number, targets: Record<MaturityDimension, number>, comment?: string) =>
    api.post<DigitalMaturityTarget[]>(`/digital-maturity/targets/bulk/${year}`, { targets, comment }),
  updateTarget: (id: string, data: Partial<CreateTargetDto & { status?: string }>) =>
    api.put<DigitalMaturityTarget>(`/digital-maturity/targets/${id}`, data),
  deleteTarget: (id: string) => api.delete(`/digital-maturity/targets/${id}`),

  // Reference data
  getDimensions: () => api.get<Record<MaturityDimension, string>>('/digital-maturity/dimensions'),
  getLevels: () => api.get<Record<MaturityLevel, string>>('/digital-maturity/levels'),

  // Directions & Measures moved to strategyApi

  // Dashboard & Analytics (kept for digital maturity assessments)
  getDashboard: () => api.get<DigitalizationDashboard>('/digital-maturity/dashboard'),
  getGapAnalysis: () => api.get<GapAnalysisItem[]>('/digital-maturity/gap-analysis'),
  getTimeline: () => api.get<TimelineMeasure[]>('/digital-maturity/timeline'),
  recalculate: () => api.post<{ success: boolean }>('/digital-maturity/recalculate'),
  seed: () => api.post<{ directions: number; measures: number }>('/digital-maturity/seed'),
}

// ==================== Activity Feed API ====================

export type NotificationType =
  | 'task_assigned'
  | 'task_status_changed'
  | 'task_deadline_approaching'
  | 'task_overdue'
  | 'task_comment_added'
  | 'gitlab_commit_linked'
  | 'ai_analysis_ready'
  | 'timer_stopped'
  | 'commit_no_time_tracking'

export interface ActivityFeedItem {
  id: string
  type: NotificationType
  title: string
  body: string | null
  data: Record<string, unknown>
  entityType: string | null
  entityId: string | null
  actorId: string | null
  actor?: {
    id: string
    displayName: string
    avatarUrl?: string
  }
  groupCount: number
  isRead: boolean
  createdAt: string
  lastEventAt: string
}

export interface ActivityFeedResponse {
  items: ActivityFeedItem[]
  total: number
  hasMore: boolean
  unreadCount: number
}

export const activityFeedApi = {
  // Get activity feed for current user
  getFeed: (params?: { page?: number; limit?: number; unreadOnly?: boolean }) =>
    api.get<ActivityFeedResponse>('/activity-feed', { params }),

  // Get unread count
  getUnreadCount: () => api.get<{ count: number }>('/activity-feed/unread-count'),

  // Get grouped events under a parent
  getGroupedEvents: (id: string) =>
    api.get<{ items: ActivityFeedItem[] }>(`/activity-feed/${id}/grouped`),

  // Mark notifications as read
  markAsRead: (ids: string[]) =>
    api.post<{ success: boolean }>('/activity-feed/mark-read', { ids }),

  // Mark all as read
  markAllAsRead: () =>
    api.post<{ success: boolean; count: number }>('/activity-feed/mark-all-read'),

  // Get pending modal alerts (not dismissed yet)
  getPendingModals: () =>
    api.get<ActivityFeedItem[]>('/activity-feed/pending-modals'),
}

// Ideas types
export type IdeaType = 'bug' | 'improvement' | 'feature' | 'research' | 'question' | 'other'
export type IdeaStatus = 'new' | 'reviewing' | 'accepted' | 'rejected' | 'quick_fix' | 'converted'
export type IdeaPriority = 'low' | 'medium' | 'high'

export interface IdeaProject {
  id: string
  ideaId: string
  projectId: string
  project: { id: string; title: string; slug: string }
  addedAt: string
}

export interface Idea {
  id: string
  title: string
  description: string | null
  type: IdeaType
  status: IdeaStatus
  priority: IdeaPriority | null
  tags: string[]
  ideaProjects: IdeaProject[]
  relatedTaskId: string | null
  relatedTask: {
    id: string
    title: string
    humanId: string
    taskProjects?: Array<{ project: { id: string; title: string } }>
  } | null
  convertedTaskId: string | null
  convertedTask: { id: string; title: string; humanId: string } | null
  authorId: string | null
  author: { id: string; fullName: string } | null
  reviewerId: string | null
  reviewer: { id: string; fullName: string } | null
  reviewedAt: string | null
  resolutionNotes: string | null
  createdAt: string
  updatedAt: string
}

export interface IdeasListResponse {
  items: Idea[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface CreateIdeaDto {
  title: string
  description?: string
  type?: IdeaType
  priority?: IdeaPriority
  tags?: string[]
  projectIds?: string[]
  relatedTaskId?: string
}

export interface UpdateIdeaDto {
  title?: string
  description?: string
  type?: IdeaType
  status?: IdeaStatus
  priority?: IdeaPriority
  tags?: string[]
  projectIds?: string[]
  relatedTaskId?: string
  resolutionNotes?: string
}

export interface ConvertIdeaDto {
  title?: string
  description?: string
  priority: 'low' | 'medium' | 'high' | 'critical' | 'urgent'
  projectId?: string
  ownerIds?: string[]
  startDate?: string
  plannedDate?: string
  hardDeadline?: string
  isStanding?: boolean
  tags?: string[]
}

export interface IdeasStats {
  total: number
  byStatus: Record<string, number>
  byType: Record<string, number>
}

export const ideasApi = {
  // Get all ideas with filters
  getAll: (params?: {
    status?: IdeaStatus
    type?: IdeaType
    authorId?: string
    projectId?: string
    search?: string
    page?: number
    limit?: number
    sortBy?: string
    sortOrder?: 'ASC' | 'DESC'
  }) => api.get<IdeasListResponse>('/ideas', { params }),

  // Get single idea
  getOne: (id: string) => api.get<Idea>(`/ideas/${id}`),

  // Get stats
  getStats: () => api.get<IdeasStats>('/ideas/stats'),

  // Create idea
  create: (data: CreateIdeaDto) => api.post<Idea>('/ideas', data),

  // Update idea
  update: (id: string, data: UpdateIdeaDto) => api.put<Idea>(`/ideas/${id}`, data),

  // Delete idea
  delete: (id: string) => api.delete(`/ideas/${id}`),

  // Convert to task
  convertToTask: (id: string, data: ConvertIdeaDto) =>
    api.post<{ id: string; humanId: string }>(`/ideas/${id}/convert`, data),
}

// ============================================================================
// Email Mailboxes (superadmin only)
// ============================================================================

export interface EmailMailbox {
  id: string
  name: string
  email: string
  imapHost: string
  imapPort: number
  imapUser: string
  // Пароли наружу не отдаются — только признаки, заданы ли они
  hasImapPassword: boolean
  useTls: boolean
  folderName: string
  enabled: boolean
  checkIntervalMinutes: number
  defaultProjectId?: string
  defaultProject?: { id: string; title: string; slug: string }
  lastCheckAt?: string
  lastCheckStatus?: 'success' | 'error'
  lastCheckError?: string
  messagesFetched: number
  // SMTP configuration
  smtpHost?: string
  smtpPort: number
  smtpUser?: string
  hasSmtpPassword: boolean
  // Notifications
  useForNotifications: boolean
  // Reply settings
  replyEnabled: boolean
  replyOnTaskCreated: boolean
  replyOnCommentAdded: boolean
  replyFormat: 'plain' | 'html'
  replyWhitelist?: string[]
  replyTemplateTaskCreated?: string
  replyTemplateCommentAdded?: string
  replyUseAi: boolean
  replyAiPrompt?: string
  createdAt: string
  updatedAt: string
}

export interface CreateMailboxDto {
  name: string
  email: string
  imapHost: string
  imapPort?: number
  imapUser: string
  imapPassword: string
  useTls?: boolean
  folderName?: string
  enabled?: boolean
  checkIntervalMinutes?: number
  defaultProjectId?: string
  // SMTP configuration
  smtpHost?: string
  smtpPort?: number
  smtpUser?: string
  smtpPassword?: string
  // Notifications
  useForNotifications?: boolean
  // Reply settings
  replyEnabled?: boolean
  replyOnTaskCreated?: boolean
  replyOnCommentAdded?: boolean
  replyFormat?: 'plain' | 'html'
  replyWhitelist?: string[]
  replyTemplateTaskCreated?: string
  replyTemplateCommentAdded?: string
  replyUseAi?: boolean
  replyAiPrompt?: string
}

export interface UpdateMailboxDto {
  name?: string
  email?: string
  imapHost?: string
  imapPort?: number
  imapUser?: string
  imapPassword?: string
  useTls?: boolean
  folderName?: string
  enabled?: boolean
  checkIntervalMinutes?: number
  defaultProjectId?: string
  // SMTP configuration
  smtpHost?: string | null
  smtpPort?: number
  smtpUser?: string | null
  smtpPassword?: string | null
  // Notifications
  useForNotifications?: boolean
  // Reply settings
  replyEnabled?: boolean
  replyOnTaskCreated?: boolean
  replyOnCommentAdded?: boolean
  replyFormat?: 'plain' | 'html'
  replyWhitelist?: string[] | null
  replyTemplateTaskCreated?: string | null
  replyTemplateCommentAdded?: string | null
  replyUseAi?: boolean
  replyAiPrompt?: string | null
}

export interface MailboxTestResult {
  success: boolean
  error?: string
  mailboxInfo?: {
    exists: number
    recent: number
    unseen: number
  }
}

export interface MailboxCheckResult {
  success: boolean
  messagesFound: number
  messagesProcessed: number
  error?: string
}

export const emailMailboxApi = {
  // Get all mailboxes
  getAll: () => api.get<EmailMailbox[]>('/email-mailboxes'),

  // Get single mailbox
  getOne: (id: string) => api.get<EmailMailbox>(`/email-mailboxes/${id}`),

  // Create mailbox
  create: (data: CreateMailboxDto) => api.post<EmailMailbox>('/email-mailboxes', data),

  // Update mailbox
  update: (id: string, data: UpdateMailboxDto) =>
    api.patch<EmailMailbox>(`/email-mailboxes/${id}`, data),

  // Delete mailbox
  delete: (id: string) => api.delete(`/email-mailboxes/${id}`),

  // Test IMAP connection
  testConnection: (id: string) =>
    api.post<MailboxTestResult>(`/email-mailboxes/${id}/test`),

  // Check mailbox for new emails now
  checkNow: (id: string) =>
    api.post<MailboxCheckResult>(`/email-mailboxes/${id}/check-now`),

  // Test SMTP connection for email replies
  testSmtpConnection: (id: string) =>
    api.post<{ success: boolean; error?: string }>(`/email-mailboxes/${id}/test-smtp`),
}

// --- Public API (no auth required, no 401 redirect) ---

const publicAxios: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
})

export interface PublicProjectInfo {
  title: string
  slug: string
  publicSubmitConfig: {
    welcomeText?: string
    allowedTags?: string[]
    requirePhone?: boolean
    allowAttachments?: boolean
    maxAttachments?: number
  }
}

export interface PublicSubmitResult {
  humanId: string
  message: string
}

export interface PublicSearchResult {
  results: Array<{ humanId: string; title: string; status: string; similarity?: number }>
  solutions: Array<{ title: string; content: string; similarity: number }>
}

export const publicApi = {
  getProjectInfo: (slug: string) =>
    publicAxios.get<PublicProjectInfo>(`/public/project/${slug}`),

  submitTicket: (slug: string, data: {
    title: string
    description: string
    uniid?: string
    contactName?: string
    contactEmail?: string
    contactPhone?: string
    department?: string
    source?: string
    tags?: string[]
    priority?: string
  }) => publicAxios.post<PublicSubmitResult>(`/public/submit/${slug}`, data),

  searchSimilar: (slug: string, query: string) =>
    publicAxios.post<PublicSearchResult>(`/public/search/${slug}`, { query }),

  suggestTitle: (slug: string, description: string) =>
    publicAxios.post<{ title: string }>(`/public/ai/suggest-title/${slug}`, { description }),
}

// === Telegram Listener ===

export interface TelegramChatConfig {
  id: string
  chatId: string
  chatTitle: string | null
  projectSlug: string
  isActive: boolean
  analysisThreshold: number
  analysisIntervalMinutes: number
  minMessagesForAnalysis: number
  lastAnalysisAt: string | null
  settings: {
    keywords?: string[]
    ignoreBots?: boolean
    dialogMode?: boolean
    minConfidence?: number
    language?: string
  }
  createdAt: string
  updatedAt: string
}

export interface TelegramDetectedIssue {
  id: string
  chatId: string
  issueType: string
  detectedTitle: string
  detectedDescription: string
  confidence: number
  messageIds: number[]
  participants: Array<{ userId: number; username?: string; firstName?: string }>
  taskId: string | null
  taskHumanId: string | null
  duplicateTaskId: string | null
  status: 'detected' | 'confirming' | 'task_created' | 'duplicate' | 'dismissed'
  dialogState: Record<string, unknown>
  aiModelUsed: string | null
  createdAt: string
}

export interface TelegramParticipant {
  userId: number
  username?: string
  firstName: string
  lastName?: string
  lastSeenAt: string
  messageCount: number
  mappedPerson?: {
    id: string
    fullName: string
    position?: string
    avatarUrl?: string
  }
}

export interface TelegramProjectParticipants {
  chats: Array<{
    id: string
    chatId: string
    chatTitle: string | null
    participants: TelegramParticipant[]
  }>
}

export const telegramListenerApi = {
  getChats: () => api.get<TelegramChatConfig[]>('/telegram-listener/chats'),
  createChat: (data: { chatId: string; projectSlug: string; chatTitle?: string; settings?: Record<string, unknown> }) =>
    api.post<TelegramChatConfig>('/telegram-listener/chats', data),
  updateChat: (id: string, data: Partial<{ isActive: boolean; analysisThreshold: number; analysisIntervalMinutes: number; minMessagesForAnalysis: number; settings: Record<string, unknown> }>) =>
    api.put<TelegramChatConfig>(`/telegram-listener/chats/${id}`, data),
  deleteChat: (id: string) => api.delete(`/telegram-listener/chats/${id}`),
  getIssues: (params?: { chatId?: string; status?: string; limit?: number }) =>
    api.get<TelegramDetectedIssue[]>('/telegram-listener/issues', { params }),
  setupWebhook: () => api.post('/telegram-listener/setup-webhook'),
  // Participant mapping
  getProjectParticipants: (slug: string) =>
    api.get<TelegramProjectParticipants>(`/telegram-listener/project/${slug}/participants`),
  mapParticipant: (data: { telegramUserId: number; personId: string }) =>
    api.post('/telegram-listener/map-participant', data),
  unmapParticipant: (telegramUserId: number) =>
    api.delete(`/telegram-listener/map-participant/${telegramUserId}`),
  syncChatParticipants: (chatConfigId: string) =>
    api.post(`/telegram-listener/chats/${chatConfigId}/sync-participants`),
}

// Analytics API
export const analyticsApi = {
  getPersonTrends: (personId: string, periodStart: string, periodEnd: string) =>
    api.get<{
      buckets: Array<{
        weekStart: string
        days: number
        taskCount: number
        taskCountNormalized: number
        avgQuality: number | null
        onTimePercent: number | null
        timeMinutes: number
        timeMinutesNormalized: number
      }>
      trends: {
        quality: 'up' | 'down' | 'stable' | 'insufficient'
        timeliness: 'up' | 'down' | 'stable' | 'insufficient'
        volume: 'up' | 'down' | 'stable' | 'insufficient'
      }
    }>('/analytics/person-trends', { params: { personId, periodStart, periodEnd } }),

  getPersonStats: (personId: string, periodStart: string, periodEnd: string) =>
    api.get<{
      taskCount: number
      avgQualityScore: number | null
      timelinessOnTime: number
      timelinessLate: number
      topCompetencies: { name: string; count: number }[]
      complexityDistribution: Record<string, number>
      totalTimeMinutes: number
      timeTrackedTasks: number
      crossPersonTasks: number
    }>('/analytics/person-stats', { params: { personId, periodStart, periodEnd } }),

  getTaskAnalyses: (personId: string, dateFrom?: string, dateTo?: string, limit?: number) =>
    api.get<Array<{
      id: string
      taskId: string
      personId: string | null
      qualityScore: number | null
      complexityLevel: string | null
      timeliness: string | null
      competencies: string[]
      strengths: string[]
      growthAreas: string[]
      summary: string | null
      analyzedAt: string
      task: { id: string; title: string; humanId: string; status: string } | null
    }>>('/analytics/task-analyses', { params: { personId, dateFrom, dateTo, limit } }),

  generatePersonReport: (personId: string, periodStart: string, periodEnd: string) =>
    api.post<{
      id: string
      personId: string
      periodStart: string
      periodEnd: string
      markdown: string
      metrics: Record<string, unknown> | null
      insights: Record<string, unknown>[]
      source: string
      taskCount: number | null
      generatedAt: string
    }>('/analytics/person-report', { personId, periodStart, periodEnd }),

  listPersonReports: (personId: string, limit?: number) =>
    api.get<Array<{
      id: string
      periodStart: string
      periodEnd: string
      markdown: string
      source: string
      model: string | null
      taskCount: number | null
      generatedAt: string
    }>>('/analytics/person-reports', { params: { personId, limit } }),

  deletePersonReport: (id: string) =>
    api.delete(`/analytics/person-report/${id}`),

  getQueueStats: () =>
    api.get<{ waiting: number; active: number; completed: number; failed: number }>('/analytics/queue-stats'),

  batchQueue: (taskIds?: string[], limit?: number) =>
    api.post<{ queued: string[]; skipped: string[]; errors: string[] }>('/analytics/batch-queue', { taskIds, limit }),

  getFailedJobs: (limit?: number) =>
    api.get<Array<{
      id: string
      taskId: string
      errorMessage: string | null
      attempt: number
      maxAttempts: number
      completedAt: string | null
      taskTitle?: string
      taskHumanId?: string
    }>>('/analytics/failed-jobs', { params: { limit } }),

  retryFailedJobs: (jobIds?: string[]) =>
    api.post<{ retried: string[]; errors: string[] }>('/analytics/retry-failed', { jobIds }),

  getUnanalyzedCount: () =>
    api.get<{ count: number; total: number }>('/analytics/unanalyzed-count'),
}
